/**
 * British malts — Crisp, Simpsons, Muntons, Thomas Fawcett and Bairds.
 *
 * UK maltsters quote colour in EBC, so EBC is the figure taken from the maker
 * and the °L alongside it is the conversion `°L = (EBC + 1.2) / 2.65` rounded
 * to one decimal — not an independent measurement. Diastatic power is published
 * either as °IoB or °WK; both are carried here as °Lintner using
 * `°Lintner = (°WK + 16) / 3.5`, which is why British pale ale malts look weak
 * next to a US 2-row even though they convert a normal grist comfortably.
 *
 * Extract is fine-grind dry basis. UK maltsters more often headline
 * `litre-degrees per kilogram` (IoB extract) instead; the percentages here are
 * the equivalent dry-basis yields. Where a maker's per-SKU spec is not
 * something we can state with confidence the entry carries class-typical
 * numbers and is marked `representative` rather than inventing precision.
 * No British maltster publishes a distilled-water mash pH, so `diPh` is `null`
 * throughout and the mash-pH model falls back to the malt class.
 *
 * ## Reading a Crisp analysis
 *
 * Crisp print every malt three times as well — an IoB column, an EBC column and
 * an ASBC one — but unlike Bairds their EBC colour column disagrees with their
 * IoB one, so neither can simply be taken. The rule here is the ASBC column:
 * colour is the **mid-point of the published ASBC °L band** and the EBC beside
 * it is derived from that with `EBC = 2.65 x °L - 1.2`, which is why several
 * Crisp rows now carry an EBC their own page does not print. Extract is the
 * EBC/ASBC percentage (fine-grind dry basis, the same number in both columns);
 * moisture is the published maximum; `suggestedMaxPercent` is their own
 * `Usage` line.
 *
 * Crisp publish **no diastatic power at all** — not on a product page, not in
 * the 2022 master specification tables, not in the gated data-sheet packs. So
 * every Crisp malt that can convert is `representative` and says in its own
 * description that the enzyme figure is class-typical, and only the malts whose
 * whole stored spec is published — the crystals, the cara malts, dextrin and
 * the roasts, all of which have zero enzyme as a matter of process — are
 * `spec-sheet`.
 *
 * Crisp also run two sites for one range: `crispmalt.com/malts/<slug>/` names a
 * crystal by EBC ("Light Crystal (Crystal 150)") and `crispmalt.com/en-us/
 * malts/<slug>/` names the same bag by °L ("Crystal 60L"). One product gets one
 * row, linked to the British slug because the American ones redirect onto it.
 * The row's name leads with the American name and carries the British one in
 * parentheses, because the brewers this catalog serves buy the bag that says
 * "Crystal 77L" and search for the number on it; the tags carry both too. The four crystals pair up as
 * Crystal 100/45L, 150/60L, 240/77L and 400/Extra Dark 120L — and Crisp's own
 * American names are approximate: the malt they sell as 77L measures 94-110
 * ASBC. Our two older crystal rows keep their ids, so `crisp-crystal-45` is the
 * Crystal 100 and `crisp-crystal-150` is the Crystal 400 its stored colour
 * always described.
 *
 * ## Reading a Bairds analysis
 *
 * Bairds print the same malt three times — an IoB column in litre-degrees, an
 * EBC column and an ASBC one — and the EBC column is the one read here, per the
 * rule above. Colour is the mid-point of its band; extract is the published
 * minimum, moisture the published maximum, diastatic power the published WK
 * minimum converted. Their `Inclusion rate` line is where the
 * `suggestedMaxPercent` figures come from, which is why every Bairds specialty
 * malt stops at five percent and the black malt at three: that is the house's
 * own guidance, not a judgement made here.
 *
 * Two pages contradict themselves across those columns and both say so in the
 * row: Maris Otter Blend gives 45 WK against 65 °Lintner, and London Lager
 * gives 70 % extract against 80.8 %. The roast end of the range — chocolate,
 * black, roasted barley — publishes colour and nothing else, so those rows
 * carry class-typical extract and moisture and stay `representative`.
 *
 * The 1823 Heritage Collection does publish a full analysis for both of its
 * malts, so both are here. The straight distilling malts are not: Bairds sell
 * Distilling Malt and High Enzyme Distilling Malt on Predicted Spirit Yield for
 * spirit production, and the organic page carries no analysis at all. The three
 * peated grades are in, because a peated malt with a full brewing analysis is a
 * brewing malt whatever the still does with it — their inclusion ceilings here
 * are brewing figures, not the 100 % Bairds quote for a mash tun feeding a
 * washback.
 *
 * ## Reading a Fawcett analysis
 *
 * Fawcett print every malt three times as well — an IoB tab, an EBC tab and an
 * ASBC one — and their two EBC columns disagree by design, the IoB figure
 * running below the EBC-method one on every page. So neither EBC column is
 * taken here: where Fawcett publish an ASBC tab the stored colour is the
 * mid-point of its °Lovibond band, which is the unit this catalog stores
 * anyway, and the EBC beside it is derived with `EBC = 2.65 x °L - 1.2`. Seven
 * of their pages carry no ASBC tab — Dextrin, Flaked Barley, Flaked Maize,
 * Mild Ale, Pale Caramalt, Red Crystal and Roast Rye — and those take the
 * mid-point of the EBC-method band back through `ebcToLovibond` instead;
 * Dextrin, which prints an IoB tab and nothing else, takes that one.
 *
 * Extract is their IoB litre-degrees per kilogram over 3.86: the fine-grind
 * figure where Fawcett publish one, which is only on the base malts, and the
 * coarse one everywhere else. That is why the specialty rows sit a point or so
 * below the round percentages their own EBC tab prints — the two are different
 * methods, not a correction of each other. Moisture is the published maximum.
 *
 * Fawcett publish **no diastatic power at all**, on any page, in any tab. Every
 * enzyme figure in a Fawcett row is therefore class-typical rather than theirs,
 * and the malts that carry zero — the crystals, the roasts, the flakes — carry
 * it as a matter of process rather than because anyone measured it. Their
 * Peated Malt page is the one that contradicts itself: its ASBC tab prints a
 * soluble nitrogen ratio and a friability that plainly belong to other rows, so
 * only the °Lovibond band on that tab is read.
 *
 * Two current Fawcett products are deliberately absent: Golden Promise
 * Distilling Malt and the malt they simply call Distilling Malt are sold on
 * Predicted Spirit Yield, and both publish the identical analysis to a pale ale
 * malt already here — the same exclusion the Bairds distilling malts get above.
 *
 * Their two oat specialties are here but had to be argued for. Crystal Oat and
 * Toasted Oat are hull-on oats at about half their weight in extract, which
 * puts them under the yield floor `fermentables.test.ts` holds barley to; both
 * are named in that file's `HULL_ON_OATS` list and in `craft-consistency.
 * test.ts`'s `OAT_MALTS`, for the reason those lists exist — an oat hull does
 * not come off in malting and yields nothing, so a barley floor asks them for
 * extract that is not physically there.
 *
 * ## Reading a Muntons analysis
 *
 * Muntons publish one small typical-analysis table per malt, and it is a PNG on
 * the product page rather than text: moisture, extract in litre-degrees per
 * kilogram off a 0.7 mm grind, sometimes total nitrogen, and wort colour in EBC.
 * That is the whole document. There is no downloadable specification pack and
 * the home-brewing side of the same site prints no figures at all, so the
 * `brewing_distilling` pages are the only source there is. Extract is the
 * published litre-degrees over 3.86, colour is the mid-point of the published
 * band or the published ceiling where they print only one, and moisture is the
 * published maximum.
 *
 * Muntons publish **no diastatic power for anything they sell**. The one place a
 * figure could have appeared — the `DP IOB` row on their distilling sheets — is
 * printed as a dash. So every Muntons malt that can convert carries a
 * class-typical °Lintner and is `representative`, and the rows that are
 * `spec-sheet` are the ones whose whole stored spec is theirs because zero
 * enzyme is a matter of process: the two cara malts, dextrin, the four
 * crystals, amber, brown, torrefied wheat and the toasted barley flakes.
 *
 * The four roasts are `representative` for a second reason. Muntons quote their
 * extract as `>= 200 lt°/kg`, a contractual floor that works out at barely half
 * what a roasted malt really yields and would drag a stout's gravity somewhere
 * it has never been. Colour and moisture on those rows are theirs, the extract
 * is class-typical, and each row says which is which.
 *
 * Muntons' grade numbers are EBC, which is the trap in this range for a brewer
 * reading °L: `Crystal Malt 240` is 240 EBC, about 91 °L, and `Cara Malt 10` is
 * a 12 EBC malt rather than a 10 °L one. The names are kept as Muntons sell
 * them and each row's description says what the number is. `muntons-crystal`
 * keeps the id it was written under when the catalog held a single unnumbered
 * Muntons crystal — the 150 EBC colour it always stored is the Crystal Malt
 * 150, so that is the product it now names.
 *
 * Oat Malt had to be argued for, the same way Fawcett's two did. Muntons publish
 * a 230 lt°/kg floor for it — 59.6 % dry basis, against the 300-plus their barley
 * malts carry — which is four tenths of a point under the extract band
 * `craft-consistency.test.ts` holds barley to; it is named in that file's
 * `OAT_MALTS` and in `fermentables.test.ts`'s `HULL_ON_OATS` for the reason those
 * lists exist. Malted Wheat Flakes are not, and are absent: they publish a
 * 220 lt°/kg floor at up to 9 % moisture, which lands under the yield floor too,
 * but wheat has no hull to blame it on, so the hull-on argument does not reach
 * them and a row exempted for a reason that is not true of it would be worse
 * than no row. Distilling Malt, Pot Still Malt and its organic twin are out for
 * the same reason the Bairds distilling malts are. The
 * three peated grades are in, but their pages publish neither colour nor enzyme,
 * so only moisture and extract on those rows are Muntons' — and their extract is
 * the as-is soluble figure put on a dry basis with the moisture beside it. The
 * liquid and dried malt extracts are a different kind of product and are not
 * malts, so they are not in this file at all.
 */

import type { Fermentable } from '../../schema/fermentable';

/** British malts. @see Fermentable */
export const ukFermentables: readonly Fermentable[] = [
  {
    id: 'bairds-heavily-peated',
    name: 'Heavily Peated Malt',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 81.5,
    moisturePercent: 5,
    colorLovibond: 2,
    colorEbc: 4,
    diastaticPowerLintner: 66,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned part way, then held in peat smoke for 65 to 70 hours, and the heaviest of the three grades Bairds peat. At 35-45 ppm phenol it is a distilling malt first: Bairds say up to 100 % because that is what a heavily peated whisky wants, and the ceiling here is a brewing figure instead. Pale and fully enzymatic, so what it changes is the smoke and nothing else.',
    tags: ['peated', 'smoked', 'scottish', 'phenolic', 'distilling'],
    url: 'https://www.bairds-malt.co.uk/distilling-malts/heavily-peated-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'crisp-amber',
    name: 'Amber Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 72.8,
    moisturePercent: 2,
    colorLovibond: 27.5,
    colorEbc: 71.7,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale malt taken up to a dry, toasted biscuit finish, and the counterweight to a sweet crystal in a mild or an old ale. Crisp cap it at 5 %, which is lower than most amber malts and worth respecting.',
    tags: ['amber', 'toasted', 'english', 'biscuit', 'dry'],
    url: 'https://crispmalt.com/malts/amber-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'crisp-dextrin',
    name: 'Dextrin Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 79.3,
    moisturePercent: 7,
    colorLovibond: 1.6,
    colorEbc: 3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A functional malt that adds body, smoothness and head without color or sweetness — the English equivalent of a Carapils. Earthy and grainy on the nose, and 5-15 % is where it earns its place.',
    tags: ['dextrin', 'body', 'english', 'head-retention', 'carapils'],
    url: 'https://crispmalt.com/malts/dextrin-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'crisp-lager-malt',
    name: 'Lager Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.6,
    moisturePercent: 4.5,
    colorLovibond: 1.75,
    colorEbc: 3.4,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'English spring barley malted pale for lager, with crusty-bread aroma and a gentle sweetness, and modified for single-infusion mashing rather than a decoction rig. Crisp publish no diastatic figure, so the one here is class-typical against their pale malts.',
    tags: ['base', 'lager', 'english', 'pilsner', 'clean'],
    url: 'https://crispmalt.com/malts/lager-malt/',
    provenance: 'representative',
  },
  {
    id: 'crisp-maris-otter',
    name: 'Maris Otter Pale Ale Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.6,
    moisturePercent: 3.5,
    colorLovibond: 2.8,
    colorEbc: 6.2,
    diastaticPowerLintner: 48,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The benchmark English pale ale malt, made from a low-yielding heritage barley that is grown because of how it tastes rather than how much it makes. Gives a full, biscuity, faintly nutty malt spine and is happy as the entire grist for bitter, mild or IPA. Crisp publish no diastatic figure for any of their malts, so the one here is class-typical.',
    tags: ['base', 'ale', 'british', 'maris-otter', 'heritage', 'bitter'],
    url: 'https://crispmalt.com/malts/finest-maris-otter-ale-malt/',
    provenance: 'representative',
  },
  {
    id: 'crisp-best-ale',
    name: 'Best Ale Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.8,
    moisturePercent: 3.5,
    colorLovibond: 2.8,
    colorEbc: 6.2,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Everyday British ale malt from modern high-yielding barley varieties. Cleaner and a touch less characterful than Maris Otter but with more enzyme in reserve, which makes it the sensible base when the grist carries a lot of adjunct. Crisp publish no diastatic figure, so that one is class-typical.',
    tags: ['base', 'ale', 'british', 'workhorse'],
    url: 'https://crispmalt.com/malts/best-ale-malt/',
    provenance: 'representative',
  },
  {
    id: 'crisp-chevallier',
    name: 'Chevallier Heritage Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 76.7,
    moisturePercent: 4.5,
    colorLovibond: 3.55,
    colorEbc: 8.2,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A revived Victorian landrace barley, the variety that most nineteenth-century British beer was brewed from. Extract and enzyme are both low by modern standards, but it repays the lost points with a deep, chewy, slightly rustic malt flavor that suits historical porters and stock ales. The extract is the published figure; the diastatic power is class-typical, because Crisp publish none.',
    tags: ['base', 'ale', 'british', 'heritage', 'historical'],
    url: 'https://crispmalt.com/malts/chevallier-heritage-malt/',
    provenance: 'representative',
  },
  {
    id: 'crisp-no-19-maris-otter',
    name: 'No. 19 Maris Otter Ale Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.6,
    moisturePercent: 4.5,
    colorLovibond: 2.6,
    colorEbc: 5.7,
    diastaticPowerLintner: 48,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Maris Otter floor-malted and kilned slowly for three days in a natural draught kiln, from certified seed off the North Norfolk mother field. A bolder, breadier version of the variety than the conventionally processed malt, and the third leg of Crisp's heritage line beside Chevallier and Hana. No diastatic figure is published, so the one here is class-typical.",
    tags: ['base', 'maris-otter', 'english', 'heritage', 'floor-malted'],
    url: 'https://crispmalt.com/malts/no-19-maris-otter-ale-malt/',
    provenance: 'representative',
  },
  {
    id: 'crisp-roast-barley',
    name: 'Roasted Barley',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 71.5,
    moisturePercent: 2,
    colorLovibond: 519,
    colorEbc: 1374.2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted barley roasted to bitter coffee and a trace of smoke, and the grain a dry stout is built on. Crisp suggest up to 5 %, and past that the roast stops being depth and starts being astringency.',
    tags: ['roasted', 'english', 'stout', 'coffee', 'smoky'],
    url: 'https://crispmalt.com/malts/roast-barley/',
    provenance: 'spec-sheet',
  },
  {
    id: 'crisp-scottish-peated',
    name: 'Scottish Peated Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.3,
    colorLovibond: 1.9,
    colorEbc: 3.8,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Wet-smoked over peat at Crisp's Speyside maltings so the smoke reaches into the kernel rather than sitting on the husk. Sold at 15, 25 and 50 ppm phenol, so what it does to a beer depends entirely on which you buy. Crisp quote up to 100 % because they are selling to distillers; the ceiling here is a brewing figure. Neither color nor diastatic power is published for it and both here are class-typical for a pale distilling malt.",
    tags: ['peated', 'smoked', 'scottish', 'phenolic', 'distilling'],
    url: 'https://crispmalt.com/malts/scottish-peated-malt/',
    provenance: 'representative',
  },
  {
    id: 'crisp-torrefied-wheat',
    name: 'Torrefied Wheat',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 81.9,
    moisturePercent: 10,
    colorLovibond: 1.6,
    colorEbc: 3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted wheat popped under heat so its starch is pre-gelatinised and available to the mash. The traditional British foam and body adjunct in cask bitter at 3-10 percent; it carries no enzyme of its own, so leave the base malt to do the converting. Crisp publish extract and moisture but no color, and the figure here is class-typical.',
    tags: ['adjunct', 'wheat', 'british', 'head-retention', 'body', 'cask'],
    url: 'https://crispmalt.com/malts/torrefied-wheat/',
    provenance: 'representative',
  },
  {
    id: 'crisp-crystal-45',
    name: 'Crystal 45L (Extra Light Crystal, 100 EBC)',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 71.8,
    moisturePercent: 5.5,
    colorLovibond: 42,
    colorEbc: 110.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Medium-light crystal with a clean toffee sweetness and a copper tint. The standard 3-8 percent seasoning that turns a pale grist into an English bitter, and mild enough not to dominate at the top of the ten percent ceiling Crisp set. Sold as Extra Light Crystal (Crystal 100) in Britain and Crystal Light 45L in the States.',
    tags: [
      'crystal',
      'caramel',
      'british',
      'toffee',
      'bitter',
      'extra-light-crystal',
      'crystal-100',
    ],
    url: 'https://crispmalt.com/malts/extra-light-crystal-crystal-100/',
    provenance: 'spec-sheet',
  },
  {
    id: 'crisp-crystal-150',
    name: 'Crystal 150L (Dark Crystal, 400 EBC)',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 71.5,
    moisturePercent: 3,
    colorLovibond: 169.5,
    colorEbc: 448,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Dark crystal pushed far enough that the caramel turns into burnt sugar, raisin and a hint of bitterness. Use it at 2-5 percent for depth in old ales, dark milds and strong bitters rather than for sweetness. This is the darkest of the four crystals Crisp sell — Dark Crystal (Crystal 400) in Britain, Crystal Extra Dark 120L in the States — and the published band puts it at 169.5 °L, a little above the 150 the id was coined from.',
    tags: [
      'crystal',
      'caramel',
      'british',
      'dark',
      'raisin',
      'dark-crystal',
      'crystal-400',
      'crystal-extra-dark-120l',
    ],
    url: 'https://crispmalt.com/malts/dark-crystal-crystal-400/',
    provenance: 'spec-sheet',
  },
  {
    id: 'crisp-brown',
    name: 'Brown Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72.8,
    moisturePercent: 2,
    colorLovibond: 51,
    colorEbc: 134,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Dry, husky and distinctly non-sweet, brown malt sits between crystal and chocolate and tastes of toast rather than cocoa. It is the malt that makes a London porter read as a porter; Crisp stop at five percent, which is the whole of the usual working range for it.',
    tags: ['roasted', 'british', 'porter', 'toast', 'historical'],
    url: 'https://crispmalt.com/malts/brown-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'crisp-chocolate',
    name: 'Chocolate Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72,
    moisturePercent: 2,
    colorLovibond: 394.5,
    colorEbc: 1044.2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale malt roasted until it smells of cocoa and dark coffee, with the husk left on. The backbone of British porter and sweet stout at up to five percent, and a fast way to add both color and roast to a dark mild.',
    tags: ['roasted', 'british', 'chocolate', 'porter', 'stout'],
    url: 'https://crispmalt.com/malts/chocolate-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'crisp-black',
    name: 'Black Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 71.5,
    moisturePercent: 2,
    colorLovibond: 488,
    colorEbc: 1292,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 3,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest of the standard British roasts: sharp, dry and tannic, with an acrid edge that is exactly what a dry stout wants. Two to four percent blackens a beer and drops mash pH noticeably, so account for it in the water calculation.',
    tags: ['roasted', 'british', 'black', 'stout', 'acrid'],
    url: 'https://crispmalt.com/malts/black-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-caramalt',
    name: 'Caramalt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 69.9,
    moisturePercent: 6.5,
    colorLovibond: 9.9,
    colorEbc: 25,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "The palest of Fawcett's crystal range, stewed for honey sweetness and a golden cast rather than for color. It brightens a golden ale or a lager without pulling it amber. Fawcett publish extract in liter-degrees per kilogram; the percentage here is that figure converted.",
    tags: ['crystal', 'caramel', 'english', 'honey', 'golden'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/caramalt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-crystal',
    name: 'Crystal Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 69.9,
    moisturePercent: 5,
    colorLovibond: 61.8,
    colorEbc: 162.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "The middle of the range and the one most English recipes mean by 'crystal': light amber with toffee and caramel sweetness, filling out the malt backbone of an ale or a bitter without dominating it.",
    tags: ['crystal', 'caramel', 'english', 'toffee', 'amber'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/crystal-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-pale-crystal',
    name: 'Pale Crystal Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 69.9,
    moisturePercent: 6.5,
    colorLovibond: 25,
    colorEbc: 65,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Sweet and malty with a soft caramel note, and the crystal a bitter or a session ale reaches for when it wants body without weight. Fawcett publish two EBC scales for it, IoB and ASBC; the color here is built from their Lovibond figure, which sits inside both.',
    tags: ['crystal', 'caramel', 'english', 'bitter', 'pale-ale'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/pale-crystal-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-roast-barley',
    name: 'Roast Barley',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68.5,
    moisturePercent: 4.5,
    colorLovibond: 547.6,
    colorEbc: 1450,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted barley roasted very dark, and drier and sharper on the finish than a black malt of similar depth. It is what makes an Irish stout taste like one, and 3-8 % is the usual rate.',
    tags: ['roasted', 'english', 'stout', 'coffee', 'dry'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/roast-barley/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-torrefied-wheat',
    name: 'Torrefied Wheat',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 72,
    moisturePercent: 10,
    colorLovibond: 1.9,
    colorEbc: 3.75,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Raw wheat puffed under heat rather than malted, which is why it brings starch, body and a durable head without any malt flavor of its own. A traditional 5-15 % in English bitters, and it needs the mash's enzymes to convert it.",
    tags: ['adjunct', 'wheat', 'english', 'head-retention', 'body'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/torrefied-wheat-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'simpsons-amber',
    name: 'Amber Malt',
    maker: 'Simpsons',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 68.7,
    moisturePercent: 2.5,
    colorLovibond: 24,
    colorEbc: 62.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Lightly roasted to toast and dry nuts rather than to caramel, which is what makes it the counterweight to a sweet crystal in a mild or an old ale. Simpsons publish extract in liter-degrees per kilogram, the British unit, and the percentage here is that figure converted.',
    tags: ['amber', 'toasted', 'english', 'biscuit', 'nutty'],
    url: 'https://www.simpsonsmalt.co.uk/our-malts/roasted-malts/amber-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'simpsons-black',
    name: 'Black Malt',
    maker: 'Simpsons',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 3,
    colorLovibond: 629.3,
    colorEbc: 1666.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Roasted hard into intense coffee and burnt grain, and one of the darkest malts in this catalog. Up to 5 % in a porter or a stout, and rather less than that if anything else roasted is already in the grist.',
    tags: ['roasted', 'black', 'english', 'porter', 'coffee'],
    url: 'https://www.simpsonsmalt.co.uk/our-malts/roasted-malts/black-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'simpsons-brown',
    name: 'Brown Malt',
    maker: 'Simpsons',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 171.8,
    colorEbc: 454,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Deeply toasted into roasted biscuit, nutty coffee and cocoa, and it sits between amber and pale chocolate on purpose: dark enough to build a porter's backbone, short of the harshness a black malt brings.",
    tags: ['roasted', 'english', 'porter', 'cocoa', 'nutty'],
    url: 'https://www.simpsonsmalt.co.uk/our-malts/roasted-malts/brown-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'simpsons-crystal-t50',
    name: 'Crystal T50',
    maker: 'Simpsons',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 55.7,
    colorEbc: 146.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "The middle of Simpsons' crystal range: honey and mild toffee with a golden-amber cast, and a clean finish rather than the raisin weight of the darker grades. Five to fifteen percent in a bitter or a pale ale, where it fills the middle without taking it over.",
    tags: ['crystal', 'caramel', 'english', 'toffee', 'amber'],
    url: 'https://www.simpsonsmalt.co.uk/our-malts/crystal-malts/crystal-t50/',
    provenance: 'spec-sheet',
  },
  {
    id: 'simpsons-dextrin',
    name: 'Dextrin Malt',
    maker: 'Simpsons',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 68,
    moisturePercent: 7,
    colorLovibond: 1.8,
    colorEbc: 3.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Body, mouthfeel and foam with a faint malty sweetness and no color at all — the lever to pull when a thin beer needs filling out and darkening it is not an option. Three to ten percent.',
    tags: ['dextrin', 'body', 'english', 'head-retention', 'mouthfeel'],
    url: 'https://www.simpsonsmalt.co.uk/our-malts/crystal-malts/dextrin-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'simpsons-golden-promise',
    name: 'Golden Promise',
    maker: 'Simpsons',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 3.5,
    colorLovibond: 2.5,
    colorEbc: 5.5,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The Scottish counterpart to Maris Otter, long favoured by distillers and by brewers of Scottish ale. Slightly sweeter and cleaner than Maris Otter with less of the biscuit note, and it takes a whole grist without complaint.',
    tags: ['base', 'ale', 'british', 'scottish', 'heritage'],
    url: 'https://www.simpsonsmalt.co.uk/our-malts/base-malts/finest-pale-ale-golden-promise/',
    provenance: 'spec-sheet',
  },
  {
    id: 'simpsons-finest-pale-ale-maris-otter',
    name: 'Finest Pale Ale Maris Otter',
    maker: 'Simpsons',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 82,
    moisturePercent: 3.5,
    colorLovibond: 2.7,
    colorEbc: 6,
    diastaticPowerLintner: 45,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Simpsons house version of Maris Otter, floor-influenced and kilned for a rounded, bready character. Interchangeable with any other Maris Otter in a recipe; the differences between maltsters are smaller than the differences between crop years.',
    tags: ['base', 'ale', 'british', 'maris-otter', 'heritage'],
    url: 'https://www.simpsonsmalt.co.uk/our-malts/base-malts/finest-pale-ale-maris-otter/',
    provenance: 'spec-sheet',
  },
  {
    id: 'simpsons-crystal-light',
    name: 'Crystal Light',
    maker: 'Simpsons',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 38.9,
    colorEbc: 102,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest of the Simpsons crystal grades, stewed gently so the sugars caramelize without browning much. Adds body and a light honeyed sweetness to golden ales where a darker crystal would muddy the color.',
    tags: ['crystal', 'caramel', 'british', 'light', 'golden-ale'],
    url: 'https://www.simpsonsmalt.co.uk/our-malts/crystal-malts/crystal-light/',
    provenance: 'representative',
  },
  {
    id: 'simpsons-crystal-medium',
    name: 'Crystal Medium',
    maker: 'Simpsons',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 5,
    colorLovibond: 60.8,
    colorEbc: 160,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The default British crystal: clear toffee and a red-amber color, with enough residual sweetness to balance a firmly hopped bitter. Five percent is the classic dose in an English pale ale.',
    tags: ['crystal', 'caramel', 'british', 'toffee', 'amber'],
    url: 'https://www.simpsonsmalt.co.uk/our-malts/crystal-malts/crystal-medium/',
    provenance: 'representative',
  },
  {
    id: 'simpsons-double-roasted-crystal',
    name: 'Double Roasted Crystal',
    maker: 'Simpsons',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 72,
    moisturePercent: 4,
    colorLovibond: 170.3,
    colorEbc: 450,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Crystal malt given a second pass through the roaster, which drives off most of the sweetness and leaves burnt caramel, dark fruit and a faint coffee edge. Widely used at 2-5 percent in porters and strong dark ales as an alternative to more roast malt.',
    tags: ['crystal', 'caramel', 'british', 'dark', 'drc', 'porter'],
    url: 'https://www.simpsonsmalt.co.uk/our-malts/crystal-malts/simpsons-drc/',
    provenance: 'representative',
  },
  {
    id: 'simpsons-chocolate',
    name: 'Chocolate Malt',
    maker: 'Simpsons',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 3,
    colorLovibond: 453.3,
    colorEbc: 1200,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Roasted to the darker end of the chocolate band, so it leans more towards coffee than cocoa. Effective at 2-5 percent where a stout needs roast depth without the harshness of black malt.',
    tags: ['roasted', 'british', 'chocolate', 'coffee', 'stout'],
    url: 'https://www.simpsonsmalt.co.uk/our-malts/roasted-malts/chocolate-malt/',
    provenance: 'representative',
  },
  {
    id: 'simpsons-golden-naked-oats',
    name: 'Golden Naked Oats',
    maker: 'Simpsons',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 7,
    colorLovibond: 7.3,
    colorEbc: 18.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A crystal malt made from huskless oats rather than barley, which is why nothing else tastes like it: breakfast-cereal sweetness, toasted nut, and a silkiness that survives into the finished beer. Five to fifteen percent is the working range, and it is the reason so many hazy pales feel thicker than their gravity suggests.',
    tags: ['oat', 'crystal', 'huskless', 'neipa', 'mouthfeel', 'nutty'],
    url: 'https://www.simpsonsmalt.co.uk/our-malts/special-malts/golden-naked-oats/',
    provenance: 'spec-sheet',
  },
  {
    id: 'simpsons-premium-english-caramalt',
    name: 'Premium English Caramalt',
    maker: 'Simpsons',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 7,
    colorLovibond: 23,
    colorEbc: 60,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The lightest of the English crystals, and the one that adds body without dragging color with it. A few percent rounds out a blonde or a session bitter, where a medium crystal would make the beer taste of toffee instead of malt.',
    tags: ['crystal', 'caramel', 'english', 'bitter', 'blonde', 'light'],
    url: 'https://www.simpsonsmalt.co.uk/our-malts/crystal-malts/premium-english-caramalt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'simpsons-imperial-malt',
    name: 'Imperial Malt',
    maker: 'Simpsons',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 17.5,
    colorEbc: 45,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned past pale ale malt and stopped short of amber: biscuit and bread crust, with more depth than a Munich and none of an amber malt\u2019s dryness. Simpsons will take it to thirty percent, which is far enough to build a bitter around rather than season one.',
    tags: ['kilned', 'biscuit', 'english', 'bitter', 'malty', 'amber'],
    url: 'https://www.simpsonsmalt.co.uk/our-malts/highly-kilned-malts/imperial-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'simpsons-crystal-extra-dark',
    name: 'Crystal Extra Dark',
    maker: 'Simpsons',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 177.5,
    colorEbc: 475,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The dark end of the English crystal range: raisin, prune and burnt toffee, arrived at by caramelizing rather than roasting, so it deepens an old ale or a porter without the ashy bitterness a roast malt brings. Simpsons caps it at ten percent and means it.',
    tags: ['crystal', 'caramel', 'dark', 'english', 'raisin', 'old-ale'],
    url: 'https://www.simpsonsmalt.co.uk/our-malts/crystal-malts/crystal-extra-dark/',
    provenance: 'spec-sheet',
  },
  {
    id: 'muntons-maris-otter-pale',
    name: 'Maris Otter Pale Ale Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79.8,
    moisturePercent: 4,
    colorLovibond: 2.3,
    colorEbc: 5,
    diastaticPowerLintner: 45,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Muntons’ standard grade of the East Anglian heritage variety: bready and nutty, fully modified, and held to 4-6 EBC so the rest of the grist still has somewhere to go. A bitter, a porter or an English IPA can be built on it alone. Muntons publish no enzyme figure for any malt they sell, so that number is class-typical.',
    tags: ['base', 'ale', 'british', 'maris-otter', 'heritage', 'bitter'],
    url: 'https://www.muntons.com/brewing_distilling/maris-otter-pale-ale-malt/',
    provenance: 'representative',
  },
  {
    id: 'muntons-maris-otter-extra-pale',
    name: 'Maris Otter Extra Pale Ale Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.3,
    moisturePercent: 5,
    colorLovibond: 2,
    colorEbc: 4,
    diastaticPowerLintner: 45,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The same heritage barley kilned shorter and capped at 4 EBC, for a pale that should read straw rather than gold. It keeps the Maris Otter cereal note and gives up some of the depth the standard grade has. The enzyme figure is class-typical; Muntons publish none.',
    tags: ['base', 'ale', 'british', 'maris-otter', 'heritage', 'low-colour'],
    url: 'https://www.muntons.com/brewing_distilling/maris-otter/',
    provenance: 'representative',
  },
  {
    id: 'muntons-maris-otter-pale-blend',
    name: 'Maris Otter Pale Blend Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79.8,
    moisturePercent: 4,
    colorLovibond: 2.3,
    colorEbc: 5,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Half Maris Otter, half modern pale ale barley, specified between the two and sold as the cheaper way to get heritage character into a beer that would not otherwise justify the sack. Analysis matches the straight Maris Otter pale ale malt; the enzyme figure is class-typical, as Muntons publish none.',
    tags: ['base', 'ale', 'british', 'maris-otter', 'blend', 'bitter'],
    url: 'https://www.muntons.com/brewing_distilling/maris-otter-pale-blend-malt/',
    provenance: 'representative',
  },
  {
    id: 'muntons-pale-ale',
    name: 'Pale Ale Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.3,
    moisturePercent: 4,
    colorLovibond: 2.3,
    colorEbc: 5,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The workhorse of the range, from modern barley and kilned a shade past the extra pale grade to a 4-6 EBC gold with bread and biscuit behind it. Neutral and enzyme-rich enough to carry sugar or flaked adjuncts alongside it. The enzyme figure is class-typical: Muntons publish none.',
    tags: ['base', 'ale', 'british', 'workhorse', 'bitter'],
    url: 'https://www.muntons.com/brewing_distilling/planet-pale-ale/',
    provenance: 'representative',
  },
  {
    id: 'muntons-extra-pale-ale',
    name: 'Extra Pale Ale Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.3,
    moisturePercent: 5,
    colorLovibond: 2,
    colorEbc: 4,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned lighter than the pale ale malt and held at 4 EBC or under, for beers whose color is meant to come from somewhere else. Gentle grain and bread rather than biscuit, and the same extract as the darker grade. The enzyme figure is class-typical, since Muntons publish none.',
    tags: ['base', 'ale', 'british', 'low-colour', 'pale'],
    url: 'https://www.muntons.com/brewing_distilling/extra-pale-malt/',
    provenance: 'representative',
  },
  {
    id: 'muntons-super-pale-ale',
    name: 'Super Pale Ale Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 6,
    colorLovibond: 1.2,
    colorEbc: 2,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest malt Muntons make, held to 2 EBC by a germination and kilning profile built for the purpose, and reckoned by them to taste like their extra pale while developing two thirds of its color. The base for a fruit beer or a heavily hopped pale that should look almost white. Enzyme figure class-typical.',
    tags: ['base', 'ale', 'british', 'low-colour', 'fruit-beer', 'ipa'],
    url: 'https://www.muntons.com/brewing_distilling/super-pale-malt/',
    provenance: 'representative',
  },
  {
    id: 'muntons-mild',
    name: 'Mild Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79.8,
    moisturePercent: 4,
    colorLovibond: 2.7,
    colorEbc: 6,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The base a proper mild is built on: British winter barley germinated and kilned to leave the wort a little less fermentable than a pale ale malt would, so the beer finishes sweeter and rounder at 5-7 EBC. Muntons also aim it at low-alcohol brewing, where body bought at the cost of attenuation is the whole point, and it takes a heavy load of adjunct. Enzyme figure class-typical.',
    tags: ['base', 'mild', 'british', 'malty', 'sweet', 'low-alcohol'],
    url: 'https://www.muntons.com/brewing_distilling/mild-malt/',
    provenance: 'representative',
  },
  {
    id: 'muntons-organic-ale',
    name: 'Organic Ale Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 75.1,
    moisturePercent: 4.5,
    colorLovibond: 4.2,
    colorEbc: 10,
    diastaticPowerLintner: 45,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Ale malt from certified organic barley, and Muntons say plainly what that costs: so little organic barley is grown that the specification has to run wider than a conventional malt’s. Lower extract and a darker 10 EBC ceiling than their pale ale malt, and the enzyme figure is class-typical.',
    tags: ['base', 'ale', 'british', 'organic'],
    url: 'https://www.muntons.com/brewing_distilling/organic-ale-malt/',
    provenance: 'representative',
  },
  {
    id: 'muntons-pilsner',
    name: 'Pilsner Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79.8,
    moisturePercent: 5,
    colorLovibond: 1.8,
    colorEbc: 3.7,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Their lager malt reworked for craft brewers: more modification, beta-glucan cut by nearly half, and a honeyed, biscuity wort under 4 EBC. Muntons make a point of its enzyme activity without ever printing a figure, so the one here is class-typical.',
    tags: ['base', 'pilsner', 'lager', 'british', 'clean'],
    url: 'https://www.muntons.com/brewing_distilling/pilsner-malt/',
    provenance: 'representative',
  },
  {
    id: 'muntons-lager',
    name: 'Lager Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79.8,
    moisturePercent: 5,
    colorLovibond: 2,
    colorEbc: 4,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The traditional lager malt the pilsner grade was developed out of — lightly kilned, quiet, and a shade under 4 EBC. A clean base for anything fermented cold, with the same extract as its newer sibling and a little more nitrogen allowed. Enzyme figure class-typical.',
    tags: ['base', 'lager', 'british', 'clean', 'pilsner'],
    url: 'https://www.muntons.com/brewing_distilling/lager-malt/',
    provenance: 'representative',
  },
  {
    id: 'muntons-wheat-malt',
    name: 'Wheat Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81.9,
    moisturePercent: 7,
    colorLovibond: 2.3,
    colorEbc: 5,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 55,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The highest-extract malt in the range, for head retention, mouthfeel and the backbone of a Belgian, German or American wheat beer. Muntons will take it to just over half the grist provided the mash and sparge are set up for grain with no husk. The enzyme figure is class-typical, as they publish none.',
    tags: ['wheat', 'base', 'british', 'head-retention', 'witbier', 'hefeweizen'],
    url: 'https://www.muntons.com/brewing_distilling/wheat-malt/',
    provenance: 'representative',
  },
  {
    id: 'muntons-rye-malt',
    name: 'Rye Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 79,
    moisturePercent: 6,
    colorLovibond: 3.1,
    colorEbc: 7,
    diastaticPowerLintner: 30,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Made in Muntons’ small drum maltings, and it brings the nutty, faintly fruity edge and the thickening mouthfeel rye is bought for. Huskless like wheat, so run-off wants the same care, and the barley malt beside it has to carry the conversion. Enzyme figure class-typical.',
    tags: ['rye', 'base', 'british', 'spicy', 'mouthfeel', 'ipa'],
    url: 'https://www.muntons.com/brewing_distilling/rye-malt/',
    provenance: 'representative',
  },
  {
    id: 'muntons-vienna',
    name: 'Vienna Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79.8,
    moisturePercent: 8,
    colorLovibond: 4,
    colorEbc: 9.5,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned past pale ale malt to 7-12 EBC and holding on to enough enzyme to work as most of a grist. Sits between pale and Munich: malty and rounded without much color, and the natural base for a Vienna lager or a golden ale that leans malt-forward. Enzyme figure class-typical.',
    tags: ['kilned', 'vienna', 'british', 'malty', 'lager'],
    url: 'https://www.muntons.com/brewing_distilling/vienna-malt/',
    provenance: 'representative',
  },
  {
    id: 'muntons-munich',
    name: 'Munich Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75.1,
    moisturePercent: 8,
    colorLovibond: 6.5,
    colorEbc: 16,
    diastaticPowerLintner: 30,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A step darker than their Vienna at 12-20 EBC, with toast, biscuit and bread in place of the Vienna’s sweetness, and a good deal less extract. Some enzyme survives, but a pale malt alongside is the safe build. Enzyme figure class-typical.',
    tags: ['kilned', 'munich', 'british', 'toasty', 'malty', 'bock'],
    url: 'https://www.muntons.com/brewing_distilling/munich-malt/',
    provenance: 'representative',
  },
  {
    id: 'muntons-amber',
    name: 'Amber Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 69.9,
    moisturePercent: 3,
    colorLovibond: 22.2,
    colorEbc: 57.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Toasted rather than roasted, and paler than the name leads you to expect at 40-75 EBC — straw-gold, with dry nuts and light toast. It is the dry counterweight to a sweet crystal in a mild, a brown ale or a porter, and it brings no enzyme of its own.',
    tags: ['amber', 'kilned', 'british', 'toasty', 'nutty', 'porter'],
    url: 'https://www.muntons.com/brewing_distilling/amber-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'muntons-brown',
    name: 'Brown Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 64.8,
    moisturePercent: 3,
    colorLovibond: 57.1,
    colorEbc: 150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A restrained brown malt at 130-170 EBC — Simpsons roast theirs three times darker and sell it under the same name. Coffee and strong toast without much ash, which makes it easier to dose into a porter or a brown ale than an English brown usually is.',
    tags: ['brown', 'kilned', 'british', 'coffee', 'toasty', 'porter'],
    url: 'https://www.muntons.com/brewing_distilling/brown-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'muntons-dextrin',
    name: 'Dextrin Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 64.8,
    moisturePercent: 7,
    colorLovibond: 3.1,
    colorEbc: 7,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Muntons’ answer to Carapils: stewed to hold on to unfermentable sugar and kilned so lightly it barely colors the beer, under 7 EBC. Body and mouthfeel for a lager, a saison or a brut IPA without the caramel a crystal malt brings. Muntons will take it to a third of the grist, and much further in a low-alcohol beer.',
    tags: ['dextrin', 'carapils', 'british', 'body', 'mouthfeel', 'low-alcohol'],
    url: 'https://www.muntons.com/brewing_distilling/dextrin-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'muntons-cara-10',
    name: 'Cara Malt 10',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78.8,
    moisturePercent: 8,
    colorLovibond: 5,
    colorEbc: 12,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest crystal Muntons make, only partly crystallized and capped at 12 EBC, so it firms up the body of even a very light beer without moving its color or its flavor. The grade number is EBC, not Lovibond. Muntons build no- and low-alcohol beers on very large doses of it, where unfermentable sugar is exactly what is wanted.',
    tags: ['cara', 'crystal', 'british', 'body', 'low-colour', 'low-alcohol'],
    url: 'https://www.muntons.com/brewing_distilling/cara-malt-10/',
    provenance: 'spec-sheet',
  },
  {
    id: 'muntons-cara-30',
    name: 'Cara Malt 30',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.1,
    moisturePercent: 8,
    colorLovibond: 12.7,
    colorEbc: 32.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A step up from the Cara 10 — gold rather than straw at 22-43 EBC, the number being EBC again — with toffee and light caramel starting to show. The gentle way into crystal character for a golden ale, a bitter or a mild.',
    tags: ['cara', 'crystal', 'british', 'toffee', 'golden-ale'],
    url: 'https://www.muntons.com/brewing_distilling/cara-malt-30/',
    provenance: 'spec-sheet',
  },
  {
    id: 'muntons-crystal-110',
    name: 'Crystal Malt 110',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 73.8,
    moisturePercent: 7,
    colorLovibond: 42,
    colorEbc: 110,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The lightest of Muntons’ four numbered crystals, and the number is EBC rather than Lovibond — about 42 °L. Copper-gold, with toffee and biscuit and little of the dark fruit the deeper grades carry. The everyday crystal for a bitter or an English IPA.',
    tags: ['crystal', 'caramel', 'british', 'toffee', 'bitter', 'ipa'],
    url: 'https://www.muntons.com/brewing_distilling/crystal-malt-110/',
    provenance: 'spec-sheet',
  },
  {
    id: 'muntons-crystal',
    name: 'Crystal Malt 150',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 72.5,
    moisturePercent: 7,
    colorLovibond: 57.1,
    colorEbc: 150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The middle of the crystal range and the one most British ale grists reach for: amber-red at 150 EBC, an EBC grade rather than a Lovibond one, with the toffee and marmalade a bitter is built around. Steeps happily for an extract batch and sits comfortably at a few percent of an all-grain one.',
    tags: ['crystal', 'caramel', 'british', 'toffee', 'marmalade', 'bitter'],
    url: 'https://www.muntons.com/brewing_distilling/crystal-malt-150/',
    provenance: 'spec-sheet',
  },
  {
    id: 'muntons-crystal-240',
    name: 'Crystal Malt 240',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 71.2,
    moisturePercent: 7,
    colorLovibond: 91,
    colorEbc: 240,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Deeper again at 240 EBC — around 91 °L, since the grade number is the British unit — with raisin and treacle joining the toffee. A small share darkens and sweetens a mild, a porter or a strong bitter without any of the bitterness a roast malt would add.',
    tags: ['crystal', 'caramel', 'british', 'dark', 'raisin', 'porter'],
    url: 'https://www.muntons.com/brewing_distilling/crystal-malt-240/',
    provenance: 'spec-sheet',
  },
  {
    id: 'muntons-crystal-400',
    name: 'Crystal Malt 400',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 69.9,
    moisturePercent: 7,
    colorLovibond: 151.4,
    colorEbc: 400,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest crystal Muntons make: 400 EBC, not 400 Lovibond, which is a little over 150 °L. Burnt toffee, prune and a suggestion of coffee, arrived at by caramelizing rather than roasting, so it deepens an old ale or a stout without the ashy edge a black malt brings.',
    tags: ['crystal', 'caramel', 'british', 'dark', 'prune', 'old-ale'],
    url: 'https://www.muntons.com/brewing_distilling/crystal-malt-400/',
    provenance: 'spec-sheet',
  },
  {
    id: 'muntons-light-chocolate',
    name: 'Light Chocolate Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 7,
    colorLovibond: 155.2,
    colorEbc: 410,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The gentler of Muntons’ two chocolate malts at 380-440 EBC: cocoa and light coffee with very little bite, and a useful first roast in a mild or a brown ale where a full chocolate malt would take the beer over. Muntons quote only an extract floor for their roasts, so the extract figure here is class-typical.',
    tags: ['roasted', 'chocolate', 'british', 'cocoa', 'mild', 'brown-ale'],
    url: 'https://www.muntons.com/brewing_distilling/light-chocolate-malt/',
    provenance: 'representative',
  },
  {
    id: 'muntons-chocolate',
    name: 'Chocolate Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 6,
    colorLovibond: 387.2,
    colorEbc: 1025,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Roasted to 850-1200 EBC for burnt coffee, cocoa nib and a firm bitterness, and Muntons keep their own recommendation to a twentieth of the grist in dark beers. The extract figure is class-typical: their roasted malts publish a contractual floor and nothing else.',
    tags: ['roasted', 'british', 'chocolate', 'coffee', 'stout', 'porter'],
    url: 'https://www.muntons.com/brewing_distilling/chocolate-malt/',
    provenance: 'representative',
  },
  {
    id: 'muntons-black',
    name: 'Black Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68,
    moisturePercent: 7,
    colorLovibond: 528.8,
    colorEbc: 1400,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 6,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest roast in the range at 1400 EBC, for color and a hard, dry char. Muntons put the working band at one to six percent and warn that the low extract and fermentability start to tell in the finished beer at the top of it; a cold steep keeps the astringency down. Extract figure class-typical.',
    tags: ['roasted', 'british', 'black', 'stout', 'colour', 'charcoal'],
    url: 'https://www.muntons.com/brewing_distilling/black-malt/',
    provenance: 'representative',
  },
  {
    id: 'muntons-roasted-barley',
    name: 'Roasted Barley',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68,
    moisturePercent: 6,
    colorLovibond: 566.5,
    colorEbc: 1500,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 6,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted barley taken to 1500 EBC, and the difference from their black malt is the starch: never modified before roasting, so it finishes drier and sharper. This is the grain an Irish dry stout is built on, and it does the same work in a Baltic porter. The extract figure is class-typical, as Muntons publish only a floor for the roasts.',
    tags: ['roasted', 'barley', 'unmalted', 'british', 'dry-stout', 'coffee'],
    url: 'https://www.muntons.com/brewing_distilling/roast-barley/',
    provenance: 'representative',
  },
  {
    id: 'muntons-oat',
    name: 'Oat Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 59.6,
    moisturePercent: 5.5,
    colorLovibond: 3.1,
    colorEbc: 7,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Husked oats from Muntons’ small drum maltings, and the husk is why the extract floor is 230 lt°/kg where their barley malts clear 300: an oat hull does not come off in malting and yields nothing. Porridge and a creamy body, and enough fatty acid to hold a stable haze without reaching for an unmalted adjunct. Muntons will take it to half the grist provided the barley malt beside it carries the conversion; the enzyme figure is class-typical, as they publish none.',
    tags: ['oat', 'malted', 'british', 'hull-on', 'body', 'neipa', 'stout'],
    url: 'https://www.muntons.com/brewing_distilling/oat-malt/',
    provenance: 'representative',
  },
  {
    id: 'muntons-torrefied-wheat',
    name: 'Torrefied Wheat',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 72.5,
    moisturePercent: 7,
    colorLovibond: 2.7,
    colorEbc: 6,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Wheat heated until the grain bursts, so it gelatinises in the mash without ever being malted. A few percent buys head retention and a firmer protein structure in a bitter; Muntons reckon three or four times that holds a stable haze in a hazy IPA. No enzyme of its own, so the barley malt has to convert it.',
    tags: ['torrefied', 'wheat', 'unmalted', 'head-retention', 'body', 'neipa'],
    url: 'https://www.muntons.com/brewing_distilling/torrefied-wheat/',
    provenance: 'spec-sheet',
  },
  {
    id: 'muntons-toasted-barley-flakes',
    name: 'Toasted Barley Flakes',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 64.8,
    moisturePercent: 13,
    colorLovibond: 1.4,
    colorEbc: 2.6,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Barley flaked and lightly toasted, pre-gelatinised so it needs no conversion of its own. Body and a clean cereal note for a stout, a cream ale or a hazy pale. The wort color is almost nothing at 2.6 EBC, though Muntons warn that a heavy dose still pulls the beer amber, and the moisture is high enough that the yield per sack is lower than the extract figure suggests.',
    tags: ['flaked', 'barley', 'toasted', 'adjunct', 'body', 'stout', 'neipa'],
    url: 'https://www.muntons.com/brewing_distilling/toasted-barley-flakes/',
    provenance: 'spec-sheet',
  },
  {
    id: 'muntons-lightly-peated',
    name: 'Whole Lightly Peated Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.6,
    moisturePercent: 4.5,
    colorLovibond: 1.6,
    colorEbc: 3,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Green malt from Muntons’ Bridlington maltings kilned in peat smoke, then blended back with unpeated distilling malt to land on about 10 ppm phenol. Subtle rather than a statement, and the malt underneath is pale and fully enzymatic, so smoke is all it changes. Muntons publish neither color nor enzyme for the peated range, so both figures here are class-typical.',
    tags: ['peated', 'smoked', 'british', 'phenolic', 'distilling'],
    url: 'https://www.muntons.com/brewing_distilling/whole-lightly-peated-malt/',
    provenance: 'representative',
  },
  {
    id: 'muntons-medium-peated',
    name: 'Whole Medium Peated Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.6,
    moisturePercent: 4.5,
    colorLovibond: 1.6,
    colorEbc: 3,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The middle grade of the peated range at about 30 ppm phenol: recognizably smoky, and enough to carry a smoked porter or a peated ale on a modest share of the grist. Color and enzyme are class-typical here because Muntons publish neither, and the extract is their as-is figure put on a dry basis.',
    tags: ['peated', 'smoked', 'british', 'phenolic', 'distilling'],
    url: 'https://www.muntons.com/brewing_distilling/whole-medium-peated-malt/',
    provenance: 'representative',
  },
  {
    id: 'muntons-heavily-peated',
    name: 'Whole Heavily Peated Malt',
    maker: 'Muntons',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.6,
    moisturePercent: 4.5,
    colorLovibond: 1.6,
    colorEbc: 3,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The heaviest grade Muntons peat, near 50 ppm phenol, and a distiller’s malt first — the page is written for whisky. In beer it wants a small share of the grist and a style that has asked for smoke. Color and enzyme figures are class-typical, since Muntons publish neither, and extract is their as-is figure converted to a dry basis.',
    tags: ['peated', 'smoked', 'british', 'phenolic', 'distilling'],
    url: 'https://www.muntons.com/brewing_distilling/whole-heavily-peated-malt/',
    provenance: 'representative',
  },
  {
    id: 'fawcett-maris-otter',
    name: 'Maris Otter Pale Ale Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 3.5,
    colorLovibond: 2.7,
    colorEbc: 6,
    diastaticPowerLintner: 45,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Maris Otter from a small Yorkshire family maltster, kilned a touch darker than most and correspondingly fuller in flavor. A favorite for traditional bitters where the malt is meant to be noticed.',
    tags: ['base', 'ale', 'british', 'maris-otter', 'heritage', 'yorkshire'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/maris-otter-pale-ale-malt/',
    provenance: 'representative',
  },
  {
    id: 'fawcett-amber',
    name: 'Amber Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 72,
    moisturePercent: 3,
    colorLovibond: 45.7,
    colorEbc: 120,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale malt roasted to a dry, biscuit-and-toast character with none of the sweetness of a crystal at the same color. Historically the mid-note in porter and old ale; 3-8 percent adds a dusty, bread-crust dryness.',
    tags: ['kilned', 'british', 'amber', 'biscuit', 'porter', 'historical'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/amber-malt/',
    provenance: 'representative',
  },
  {
    id: 'fawcett-crystal-rye',
    name: 'Crystal Rye Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 72,
    moisturePercent: 5,
    colorLovibond: 72.2,
    colorEbc: 190,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Rye stewed and kilned like a crystal malt, pairing caramel sweetness with rye pepper and a reddish cast. Two to seven percent lifts a rye pale ale or adds an unexpected spice edge to a brown ale.',
    tags: ['crystal', 'caramel', 'rye', 'british', 'spicy', 'red'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/crystal-rye-malt/',
    provenance: 'representative',
  },
  {
    id: 'fawcett-pale-chocolate',
    name: 'Pale Chocolate Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 3,
    colorLovibond: 208,
    colorEbc: 550,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Roasted only about half as far as standard chocolate malt, so it reads as milk chocolate and toasted nut rather than coffee. Ideal for brown ales and softer porters where full chocolate malt would be too sharp.',
    tags: ['roasted', 'british', 'chocolate', 'brown-ale', 'smooth'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/pale-chocolate-malt/',
    provenance: 'representative',
  },
  {
    id: 'bairds-pale-ale',
    name: 'Pale Ale Malt (Maris Otter)',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81.5,
    moisturePercent: 3.5,
    colorLovibond: 2.5,
    colorEbc: 5.5,
    diastaticPowerLintner: 45,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Maris Otter malted in Scotland by a maltster that mostly supplies distilleries, so the specification runs consistent and well-modified. Behaves like any other Maris Otter in the mash tun.',
    tags: ['base', 'ale', 'british', 'maris-otter', 'scottish'],
    url: 'https://www.bairds-malt.co.uk/base-malts/maris-otter-malt/',
    provenance: 'representative',
  },
  {
    id: 'bairds-lager',
    name: 'Lager Malt',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 4,
    colorLovibond: 1.8,
    colorEbc: 3.5,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Lightly kilned British lager malt: pale, clean and higher in enzyme than the ale malts. A useful neutral base for lagers and for pale hop-forward ales where a British malt character is not wanted.',
    tags: ['base', 'lager', 'british', 'scottish', 'pale'],
    url: 'https://www.bairds-malt.co.uk/base-malts/lager-malt/',
    provenance: 'representative',
  },
  {
    id: 'bairds-carastan',
    name: 'Carastan',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 34.4,
    colorEbc: 90,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A light crystal malt sold in a couple of closely spaced color grades, drier and less obviously sweet than a typical British crystal. Adds body, a golden-amber hue and a subtle nutty note at 3-8 percent.',
    tags: ['crystal', 'caramel', 'british', 'light', 'nutty'],
    provenance: 'representative',
  },
  {
    id: 'bairds-roasted-barley',
    name: 'Roasted Barley',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 66,
    moisturePercent: 3,
    colorLovibond: 472.2,
    colorEbc: 1250,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 6,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted barley taken all the way to a dark roast, which is what separates an Irish dry stout from a porter. Gives sharp espresso bitterness and the classic ruby-black color; 5-10 percent of the grist is the traditional dose.',
    tags: ['roasted', 'british', 'unmalted', 'stout', 'coffee', 'dry-stout'],
    url: 'https://www.bairds-malt.co.uk/roasted-malts/roasted-barley/',
    provenance: 'representative',
  },
  {
    id: 'bairds-pale-ale-standard',
    name: 'Pale Ale Malt',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4,
    colorLovibond: 2.5,
    colorEbc: 5.5,
    diastaticPowerLintner: 44.6,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The everyday English pale ale malt, made from whatever the crop year gives rather than from Maris Otter alone, and specified to the same 4.4-6.6 EBC and 140 WK as the named-variety grade. Bairds will take it to 100 % of anything from a bitter to a porter.',
    tags: ['base', 'ale', 'pale-ale', 'english', 'british'],
    url: 'https://www.bairds-malt.co.uk/base-malts/pale-ale-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bairds-extra-pale-ale',
    name: 'Extra Pale Ale Malt',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4,
    colorLovibond: 1.8,
    colorEbc: 3.45,
    diastaticPowerLintner: 44.6,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted at Witham for Loughran and kilned to 2.5-4.4 EBC, which puts it below their own lager malt — the point of it is that a hazy pale or an XPA can be built entirely from ale malt without the color creeping up. Same 140 WK as the standard grade.',
    tags: ['base', 'ale', 'pale-ale', 'extra-pale', 'hazy', 'british'],
    url: 'https://www.bairds-malt.co.uk/base-malts/extra-pale-ale-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bairds-golden-promise',
    name: 'Golden Promise Malt',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 1.7,
    colorEbc: 3.4,
    diastaticPowerLintner: 66,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The Scottish heritage variety that Scotch whisky and Scottish ale were built on, malted pale at 2.8-4.0 EBC. Extract is a point or two below a modern variety and the enzyme is high at 215 WK; what is bought is the sweet, faintly stone-fruited wort it makes.',
    tags: ['base', 'ale', 'golden-promise', 'heritage', 'scottish', 'british'],
    url: 'https://www.bairds-malt.co.uk/base-malts/golden-promise-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bairds-maris-otter-blend',
    name: 'Maris Otter Blend',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.5,
    moisturePercent: 4,
    colorLovibond: 2.5,
    colorEbc: 5.5,
    diastaticPowerLintner: 45,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Maris Otter blended with other ale varieties to hold the specification steady across a crop year — the same 4.4-6.6 EBC and 80.5 % extract as the pure lot, at a price a brewery can plan around. The two analysis columns on its page disagree about the enzyme, 45 WK against 65 °Lintner, so the figure here is the 45 °Lintner their straight Maris Otter carries.',
    tags: ['base', 'ale', 'maris-otter', 'blend', 'british', 'biscuit'],
    url: 'https://www.bairds-malt.co.uk/base-malts/maris-otter-blend/',
    provenance: 'representative',
  },
  {
    id: 'bairds-scottish-ale',
    name: 'Scottish Ale Malt',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 82,
    moisturePercent: 4.5,
    colorLovibond: 1.7,
    colorEbc: 3.4,
    diastaticPowerLintner: 44.6,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'At 82 % dry-basis extract the highest-yielding malt Bairds publish, kilned pale to 2.8-4.0 EBC and low in nitrogen. Sweet, clean wort for Scottish ales, and Bairds are happy to see it in a porter too.',
    tags: ['base', 'ale', 'scottish', 'high-extract', 'british'],
    url: 'https://www.bairds-malt.co.uk/base-malts/scottish-ale-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bairds-1823-maris-otter',
    name: 'Maris Otter Finest Ale Malt (1823 Heritage Collection)',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81.3,
    moisturePercent: 4,
    colorLovibond: 2.7,
    colorEbc: 6,
    diastaticPowerLintner: 51.7,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The Heritage Collection grade of Maris Otter: a point higher in extract than the standard lot, kilned a shade darker to 5-7 EBC, tighter on nitrogen and stronger on enzyme at 165 WK. Sweet biscuit for a best bitter, at up to the whole grist.',
    tags: ['base', 'ale', 'maris-otter', 'heritage', '1823', 'british', 'biscuit'],
    url: 'https://www.bairds-malt.co.uk/the-1823-heritage-collection/maris-otter-finest-ale-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bairds-1823-london-lager',
    name: 'London Lager Malt (1823 Heritage Collection)',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.8,
    moisturePercent: 4.5,
    colorLovibond: 1.9,
    colorEbc: 3.75,
    diastaticPowerLintner: 74.6,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A lager malt in the English manner, kilned to 3-4.5 EBC and holding 245 WK, with a malty, honeyed wort rather than the flat neutrality a continental pilsner malt aims at. Its page prints extract twice and the two disagree — 70 % in the EBC column against 80.8 % in the ASBC one — and the higher figure is the only one a lager malt could meet.',
    tags: ['base', 'lager', 'pilsner', 'heritage', '1823', 'british', 'honey'],
    url: 'https://www.bairds-malt.co.uk/the-1823-heritage-collection/london-lager-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bairds-vienna',
    name: 'Vienna Malt',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80.5,
    moisturePercent: 3.5,
    colorLovibond: 3.8,
    colorEbc: 9,
    diastaticPowerLintner: 46,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned to 8-10 EBC and still holding 145 WK, so it converts itself — but Bairds put the inclusion rate at 10-15 % rather than the whole grist a continental Vienna would claim, and that is the ceiling here. Golden to orange, with a sweet wort behind it.',
    tags: ['kilned', 'vienna', 'british', 'golden', 'malty'],
    url: 'https://www.bairds-malt.co.uk/highly-kilned-malts/vienna-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bairds-dextrin',
    name: 'Dextrin Malt',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 7,
    colorLovibond: 1.9,
    colorEbc: 3.75,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A carapils-type malt at 3-4.5 EBC, bought for foam, body and palate fullness rather than for anything you can taste. Extract is a modest 70 % by design — the sugars it carries are meant not to ferment. Bairds put it at 5-15 % of the grist.',
    tags: ['dextrin', 'british', 'body', 'head-retention', 'carapils'],
    url: 'https://www.bairds-malt.co.uk/other-malts/dextrin-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bairds-light-caramalt',
    name: 'Light Caramalt',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77,
    moisturePercent: 7,
    colorLovibond: 9.9,
    colorEbc: 25,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest of the Bairds caramels at 15-35 EBC, made for pale lagers that need a little color and sweetness and no more. Toffee and burnt sugar at a golden depth; Bairds cap the whole specialty range at 5 %.',
    tags: ['crystal', 'caramel', 'light', 'british', 'golden', 'lager'],
    url: 'https://www.bairds-malt.co.uk/roasted-malts/light-caramalt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bairds-medium-caramalt',
    name: 'Medium Caramalt',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77,
    moisturePercent: 6,
    colorLovibond: 25,
    colorEbc: 65,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A step darker at 55-75 EBC and drier as shipped, sitting between the caramalts and the crystals proper. Toffee, caramel and raisin in a pale lager or a light ale, up to 5 %.',
    tags: ['crystal', 'caramel', 'british', 'toffee', 'amber'],
    url: 'https://www.bairds-malt.co.uk/roasted-malts/medium-caramalt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bairds-light-crystal',
    name: 'Light Crystal Malt',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 36.3,
    colorEbc: 95,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The first of the four crystal grades at 80-110 EBC, and the one that gives a British bitter its color and its nutty caramel sweetness. Bairds hold the whole ladder to 5 % of the grist.',
    tags: ['crystal', 'caramel', 'british', 'bitter', 'toffee'],
    url: 'https://www.bairds-malt.co.uk/roasted-malts/light-crystal-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bairds-medium-crystal',
    name: 'Medium Crystal Malt',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 57.1,
    colorEbc: 150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The middle crystal at 140-160 EBC, deeper into treacle and stewed fruit than the light grade and copper rather than amber in the glass. The default for a pale ale that wants malt as well as hops.',
    tags: ['crystal', 'caramel', 'british', 'bitter', 'copper'],
    url: 'https://www.bairds-malt.co.uk/roasted-malts/medium-crystal-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bairds-dark-crystal',
    name: 'Dark Crystal Malt',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 78.8,
    colorEbc: 207.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'At 185-230 EBC the red hues Bairds talk about arrive in earnest, and so do raisin and treacle over the caramel. Two or three percent is usually enough; five is their ceiling.',
    tags: ['crystal', 'caramel', 'dark', 'british', 'raisin', 'red'],
    url: 'https://www.bairds-malt.co.uk/roasted-malts/dark-crystal-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bairds-extra-dark-crystal',
    name: 'Extra Dark Crystal Malt',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 117.4,
    colorEbc: 310,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The end of the crystal ladder at 270-350 EBC, where the sweetness turns to burnt sugar and the color does most of the talking. A percent or two in an old ale or a strong bitter.',
    tags: ['crystal', 'caramel', 'very-dark', 'british', 'treacle'],
    url: 'https://www.bairds-malt.co.uk/roasted-malts/extra-dark-crystal-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bairds-amber',
    name: 'Amber Malt',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 36.3,
    colorEbc: 95,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Toasted rather than stewed, which is why it reads dry and biscuity at 85-105 EBC where a crystal of the same color would read sweet. The traditional seasoning for a mild, a bitter or a sweet stout, at up to 5 %.',
    tags: ['kilned', 'amber', 'british', 'biscuit', 'dry', 'mild'],
    url: 'https://www.bairds-malt.co.uk/roasted-malts/amber-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bairds-brown',
    name: 'Brown Malt',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 45.7,
    colorEbc: 120,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A step past amber at 110-130 EBC into a dry, mild coffee note, and the malt an English porter is historically built around. It carries no enzyme, so mash it against a base malt; Bairds stop at 5 %.',
    tags: ['kilned', 'brown', 'british', 'coffee', 'dry', 'porter'],
    url: 'https://www.bairds-malt.co.uk/roasted-malts/brown-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bairds-chocolate',
    name: 'Chocolate Malt',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 377.8,
    colorEbc: 1000,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Roasted to 900-1100 EBC for cocoa, toast and a sharp roast bitterness, and the backbone of an English porter or a sweet stout. Bairds publish color for it and nothing else, so the extract and moisture here are class-typical for a chocolate malt.',
    tags: ['roasted', 'chocolate', 'british', 'cocoa', 'porter', 'stout'],
    url: 'https://www.bairds-malt.co.uk/roasted-malts/chocolate-malt/',
    provenance: 'representative',
  },
  {
    id: 'bairds-black',
    name: 'Black Malt',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 509.9,
    colorEbc: 1350,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 3,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The far end of the drum at 1200-1500 EBC, and Bairds are blunt about it: burnt coffee, burnt, acrid. Three percent is their ceiling and one usually does the coloring. Color is the only figure they publish, so extract and moisture here are class-typical for a black malt.',
    tags: ['roasted', 'black', 'british', 'colour-malt', 'acrid', 'stout'],
    url: 'https://www.bairds-malt.co.uk/roasted-malts/black-malt/',
    provenance: 'representative',
  },
  {
    id: 'bairds-lightly-peated',
    name: 'Lightly Peated Malt',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 81.5,
    moisturePercent: 5,
    colorLovibond: 2,
    colorEbc: 4,
    diastaticPowerLintner: 66,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The bottom rung of the Bairds peating ladder at 10-15 ppm of phenol in the malt, which carries 2-5 ppm into a spirit and rather more into a beer. Pale, high in extract and fully enzymatic at 215 WK, so the smoke is all it changes. Bairds say up to 100 % because that is what a whisky wants; the ceiling here is a brewing figure.',
    tags: ['peated', 'smoked', 'scottish', 'phenolic', 'distilling', 'british'],
    url: 'https://www.bairds-malt.co.uk/distilling-malts/lightly-peated-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bairds-medium-peated',
    name: 'Medium Peated Malt',
    maker: 'Bairds',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 81.5,
    moisturePercent: 5,
    colorLovibond: 2,
    colorEbc: 4,
    diastaticPowerLintner: 66,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The middle grade at 20-30 ppm of phenol, on an analysis otherwise identical to the lightly peated malt — the peat is the only variable Bairds move. Medicinal and maritime rather than woody, and it takes over a beer at a dose that would barely register in a cask. The ceiling here is a brewing figure; Bairds quote 100 % for the still.',
    tags: ['peated', 'smoked', 'scottish', 'phenolic', 'distilling', 'british'],
    url: 'https://www.bairds-malt.co.uk/distilling-malts/medium-peated-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'crisp-extra-pale-maris-otter',
    name: 'Extra Pale Maris Otter',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.6,
    moisturePercent: 4.5,
    colorLovibond: 1.7,
    colorEbc: 3.3,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Maris Otter kilned as pale as the variety will go, so the bready depth survives but the color does not. It exists for the modern pale ale and IPA that wants an English base malt without the amber tint standard Maris Otter drags in. Crisp publish no diastatic figure, so the one here is class-typical.',
    tags: ['maris-otter', 'base', 'english', 'extra-pale', 'ipa'],
    url: 'https://crispmalt.com/malts/extra-pale-maris-otter-malt/',
    provenance: 'representative',
  },
  {
    id: 'crisp-naked-oat-malt',
    name: 'Naked Oat Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 65,
    moisturePercent: 5.5,
    colorLovibond: 2.35,
    colorEbc: 5,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A huskless oat, malted rather than flaked, and the reason so many hazy IPAs hold their haze: it brings beta-glucan and soluble protein in quantity, which is body and stability at once. Crisp will take it to thirty percent, though the mash gets slow long before that — rice hulls past ten. The diastatic power is class-typical; Crisp publish extract, color and β-glucan for it but no enzyme figure.',
    tags: ['oat', 'huskless', 'neipa', 'haze', 'mouthfeel', 'adjunct'],
    url: 'https://crispmalt.com/malts/naked-oat-malt/',
    provenance: 'representative',
  },
  {
    id: 'crisp-clear-choice-pale-malt',
    name: 'Clear Choice Pale Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.6,
    moisturePercent: 4.5,
    colorLovibond: 2.1,
    colorEbc: 4.4,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted from a proanthocyanidin-free barley, so the polyphenols that bind with protein and drop out cold are simply not there. The point is a beer that stays bright without finings, silica or a long cold conditioning — chill haze prevented at the grist rather than filtered out later. The diastatic power is class-typical, as Crisp publish none.',
    tags: ['base', 'pale', 'clear-choice', 'haze-free', 'polyphenol-free', 'english'],
    url: 'https://crispmalt.com/malts/clear-choice-pale-malt/',
    provenance: 'representative',
  },
  {
    id: 'crisp-hana-malt',
    name: 'Hana Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 76.7,
    moisturePercent: 4.5,
    colorLovibond: 1.7,
    colorEbc: 3.3,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The Moravian landrace behind the first golden lager, malted again: lower extract than any modern cultivar and a rounder, honeyed malt flavor in exchange. For a Bohemian pilsner that is meant to taste of 1842 rather than of this year\u2019s barley. Crisp publish no diastatic figure, so the one here is class-typical.',
    tags: ['heritage', 'hana', 'landrace', 'pilsner', 'bohemian', 'base'],
    url: 'https://crispmalt.com/malts/hana-malt/',
    provenance: 'representative',
  },
  {
    id: 'crisp-extra-pale-ale',
    name: 'Extra Pale Ale Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.8,
    moisturePercent: 4.5,
    colorLovibond: 1.7,
    colorEbc: 3.3,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Best Ale Malt kilned short of any color: the same barley and the same extract, stopped at 1.5-1.9 ASBC so a blonde or a lager built on it stays straw rather than gold. The cleanest English base malt Crisp make, and the one to reach for when the hops are meant to do the talking. No diastatic figure is published, so the one here is class-typical.',
    tags: ['base', 'ale', 'english', 'extra-pale', 'lager', 'blonde'],
    url: 'https://crispmalt.com/malts/extra-pale-ale-malt/',
    provenance: 'representative',
  },
  {
    id: 'crisp-scottish-pale-ale',
    name: 'Scottish Pale Ale Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 82,
    moisturePercent: 3.5,
    colorLovibond: 2.9,
    colorEbc: 6.5,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Scottish spring barley malted at Portgordon, and the highest-extract ale malt in the Crisp range at 82 % dry basis. Color and nitrogen sit exactly where an English pale ale malt does, so it substitutes one for one and simply gives a point or two more gravity. Diastatic power is class-typical; Crisp publish none.',
    tags: ['base', 'ale', 'scottish', 'pale', 'high-extract'],
    url: 'https://crispmalt.com/malts/scottish-pale-ale-malt/',
    provenance: 'representative',
  },
  {
    id: 'crisp-scottish-extra-pale-ale',
    name: 'Scottish Extra Pale Ale Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 82,
    moisturePercent: 4.5,
    colorLovibond: 1.75,
    colorEbc: 3.4,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The low-color grade of the Scottish pale ale malt, kilned to under 4 EBC while keeping the same 82 % extract. Built for golden ales, blondes and lagers that want a British base malt and no color from it at all. The diastatic figure is class-typical, as Crisp publish none.',
    tags: ['base', 'ale', 'scottish', 'extra-pale', 'lager', 'high-extract'],
    url: 'https://crispmalt.com/malts/scottish-extra-pale-ale-malt/',
    provenance: 'representative',
  },
  {
    id: 'crisp-german-pilsen',
    name: 'German Pilsen Malt',
    maker: 'Crisp',
    origin: 'Germany',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79.3,
    moisturePercent: 4.5,
    colorLovibond: 1.9,
    colorEbc: 3.8,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Made at the Tivoli maltings in Hamburg rather than in Norfolk, and specified as a continental pilsner malt: lower extract than the English base malts, more nitrogen, and a modification range that suits a decoction or a step mash. The Crisp malt to use when the recipe says German pilsner and means it. No enzyme figure is published, so the one here is class-typical.',
    tags: ['base', 'pilsner', 'german', 'lager', 'continental'],
    url: 'https://crispmalt.com/malts/german-pilsen-malt/',
    provenance: 'representative',
  },
  {
    id: 'crisp-vienna',
    name: 'Vienna Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79.8,
    moisturePercent: 4.5,
    colorLovibond: 3.35,
    colorEbc: 7.7,
    diastaticPowerLintner: 46,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned a shade past pale on a conventional kiln, which is enough for a light golden hue and a soft bready sweetness without any of the toast a Munich brings. Crisp will take it to the whole grist, and a Vienna lager or an altbier is happier for it. Diastatic power is class-typical.',
    tags: ['vienna', 'kilned', 'lager', 'altbier', 'kolsch', 'golden'],
    url: 'https://crispmalt.com/malts/vienna-malt/',
    provenance: 'representative',
  },
  {
    id: 'crisp-light-munich',
    name: 'Light Munich Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79.3,
    moisturePercent: 6,
    colorLovibond: 8.85,
    colorEbc: 22.3,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Conventionally kilned to a golden-orange, and the point at which the malt starts tasting of itself: bread crust, a little honey, and enough color to turn a lager amber at 30 %. Retains enough enzyme to convert its own starch, so a Marzen can be built almost entirely on it. The diastatic figure is class-typical, as Crisp publish none.',
    tags: ['munich', 'kilned', 'marzen', 'maibock', 'malty', 'golden'],
    url: 'https://crispmalt.com/malts/light-munich-malt/',
    provenance: 'representative',
  },
  {
    id: 'crisp-dark-munich',
    name: 'Dark Munich Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 17,
    colorEbc: 43.9,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Munich taken further up the kiln until the bread crust turns to toast and dried fruit. Crisp recommend 15-20 %, which is unusually restrained for a Munich and reflects how much color and melanoidin it carries. Enzyme is low but not absent; the figure here is class-typical because none is published.',
    tags: ['munich', 'kilned', 'dunkel', 'bock', 'malty', 'melanoidin'],
    url: 'https://crispmalt.com/malts/dark-munich-malt/',
    provenance: 'representative',
  },
  {
    id: 'crisp-wheat-malt',
    name: 'Wheat Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81.9,
    moisturePercent: 6.5,
    colorLovibond: 2,
    colorEbc: 4.1,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted English wheat, high in protein and high in extract, and the fastest way to firm up foam in any beer at 5 %. Crisp will go to half the grist for a hefeweizen or a wit, with the warning that comes with every wheat malt: no husk, so give the mash rice hulls and the run-off some patience. Diastatic power is class-typical.',
    tags: ['wheat', 'base', 'english', 'hefeweizen', 'wit', 'head-retention'],
    url: 'https://crispmalt.com/malts/wheat-malt/',
    provenance: 'representative',
  },
  {
    id: 'crisp-rye',
    name: 'Rye Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 6,
    colorLovibond: 9.7,
    colorEbc: 24.5,
    diastaticPowerLintner: 30,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted rye, for the dry peppery snap that no barley malt gives and that a rye IPA or a Roggenbier is built around. Crisp cap it at 15 %, and the reason is the mash rather than the flavor: rye is huskless and gluey, and a lauter tun notices well before the palate objects. Crisp publish color and moisture but leave the extract line blank, so the extract and diastatic figures here are class-typical.',
    tags: ['rye', 'base', 'english', 'roggenbier', 'spicy', 'rye-ipa'],
    url: 'https://crispmalt.com/malts/rye-malt/',
    provenance: 'representative',
  },
  {
    id: 'crisp-cara-gold',
    name: 'Cara Gold Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74.1,
    moisturePercent: 6.5,
    colorLovibond: 6,
    colorEbc: 14.7,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest of the stewed malts Crisp make: body, foam and a soft toffee sweetness at a color that barely moves a golden ale off gold. Where a dextrin malt gives structure and no flavor, this gives both, which is why it goes to 15 % without darkening anything.',
    tags: ['cara', 'crystal', 'caramel', 'english', 'body', 'head-retention', 'golden-ale'],
    url: 'https://crispmalt.com/malts/cara-gold-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'crisp-cara-malt',
    name: 'Cara Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 73.3,
    moisturePercent: 6,
    colorLovibond: 12,
    colorEbc: 30.6,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A shade darker than Cara Gold and doing the same job with more caramel behind it. The usual home is a lager or a low-alcohol beer that needs the body it lost with the gravity, and 15 % is as far as Crisp will take it.',
    tags: ['cara', 'crystal', 'caramel', 'english', 'body', 'lager', 'low-alcohol'],
    url: 'https://crispmalt.com/malts/cara-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'crisp-crystal-60',
    name: 'Crystal 60L (Light Crystal, 150 EBC)',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 71.8,
    moisturePercent: 5,
    colorLovibond: 65,
    colorEbc: 171.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The second of the four Crisp crystals, and the one most English bitters actually want: sweet caramel, a golden-amber hue, and enough of it at 5 % to read as malt rather than as sugar. Sold as Light Crystal (Crystal 150) in Britain and Crystal 60L in the States.',
    tags: [
      'crystal',
      'caramel',
      'british',
      'toffee',
      'bitter',
      'light-crystal',
      'crystal-150',
      'crystal-60l',
    ],
    url: 'https://crispmalt.com/malts/light-crystal-crystal-150/',
    provenance: 'spec-sheet',
  },
  {
    id: 'crisp-crystal-77',
    name: 'Crystal 77L (Medium Crystal, 240 EBC)',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 71.5,
    moisturePercent: 3.5,
    colorLovibond: 102,
    colorEbc: 269.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The third Crisp crystal, where deep caramel starts turning towards dark fruit and the hue goes from amber to red. Crisp point it at best bitters, milds and porters and specifically not at stouts, where the roast has the job instead. Sold as Medium Crystal (Crystal 240) in Britain and Crystal 77L in the States, and the published band is 94-110 ASBC rather than the 77 the American name suggests.',
    tags: [
      'crystal',
      'caramel',
      'british',
      'dark',
      'red',
      'medium-crystal',
      'crystal-240',
      'crystal-77l',
    ],
    url: 'https://crispmalt.com/malts/medium-crystal-crystal-240/',
    provenance: 'spec-sheet',
  },
  {
    id: 'crisp-pale-chocolate',
    name: 'Pale Chocolate Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72,
    moisturePercent: 2,
    colorLovibond: 207.5,
    colorEbc: 548.7,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Chocolate malt pulled off the roaster early, at roughly half the color of the full grade. It gives the cocoa and the coffee without the sharp edge, which is what a brown ale or a milk stout wants when the recipe needs roast character but not roast bitterness. Sold as Low Colour Chocolate Malt in Britain and Pale Chocolate in the States.',
    tags: ['roasted', 'british', 'chocolate', 'porter', 'stout', 'low-colour-chocolate'],
    url: 'https://crispmalt.com/malts/low-colour-chocolate-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'crisp-flaked-torrefied-barley',
    name: 'Flaked Torrefied Barley',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 81.4,
    moisturePercent: 10,
    colorLovibond: 1.5,
    colorEbc: 2.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted barley popped and rolled, so it goes into the mash without milling and gives up its starch to the base malt. Foam and body in any beer at 5 %, and the grainy bite an Irish stout is built on. Crisp publish extract and moisture but no color, so the color here is class-typical.',
    tags: ['adjunct', 'barley', 'flaked', 'torrefied', 'british', 'head-retention', 'stout'],
    url: 'https://crispmalt.com/malts/flaked-torrefied-barley/',
    provenance: 'representative',
  },
  {
    id: 'crisp-flaked-torrefied-maize',
    name: 'Flaked Torrefied Maize',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 86.5,
    moisturePercent: 8.5,
    colorLovibond: 1,
    colorEbc: 1.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pre-cooked maize flakes, and the highest-yielding thing in the Crisp range at 86.5 % dry basis — maize is nearly all starch and almost no protein, so the extract runs above what barley can reach. Thins the body and lifts the gravity at once, which is exactly what a pre-Prohibition lager or a light continental lager is after. No color is published; the figure here is class-typical.',
    tags: ['adjunct', 'maize', 'corn', 'flaked', 'torrefied', 'lager', 'light-body'],
    url: 'https://crispmalt.com/malts/flaked-torrefied-maize/',
    provenance: 'representative',
  },
  {
    id: 'crisp-flaked-torrefied-oats',
    name: 'Flaked Torrefied Oats',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 77.2,
    moisturePercent: 11,
    colorLovibond: 1,
    colorEbc: 1.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Oats pressure-cooked and rolled, which is the silky mouthfeel of an oatmeal stout and half the haze of a New England IPA in one addition. The 11 % moisture is the highest of anything Crisp sell and pulls the as-is yield down accordingly. Color is not published, so it is class-typical here.',
    tags: ['adjunct', 'oats', 'flaked', 'torrefied', 'neipa', 'stout', 'mouthfeel'],
    url: 'https://crispmalt.com/malts/flaked-torrefied-oats/',
    provenance: 'representative',
  },
  {
    id: 'crisp-flaked-torrefied-rice',
    name: 'Flaked Torrefied Rice',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 80.6,
    moisturePercent: 8.5,
    colorLovibond: 0.5,
    colorEbc: 0.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The most neutral adjunct there is: rice flakes add gravity, take out body and contribute essentially nothing to color or flavor. A quarter of the grist is where Crisp stop, and that is enough to make a light lager crisp and dry or to cut the gluten load of a beer. Color is class-typical, as none is published.',
    tags: ['adjunct', 'rice', 'flaked', 'torrefied', 'lager', 'light-body', 'neutral'],
    url: 'https://crispmalt.com/malts/flaked-torrefied-rice/',
    provenance: 'representative',
  },
  {
    id: 'crisp-scottish-premium-pot-still',
    name: 'Scottish Premium Pot Still Malt',
    maker: 'Crisp',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.3,
    colorLovibond: 1.9,
    colorEbc: 3.8,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The unpeated half of the Portgordon distilling range, and the malt the peated grades are made from. Crisp specify it for the still rather than the kettle — 410 liters of alcohol per metric ton, 87 % fermentability, 93 % friability — so it is a very well modified, very pale, very fermentable base malt with the flavor deliberately left out. Neither color nor diastatic power is published; both here are class-typical.',
    tags: ['base', 'scottish', 'distilling', 'pot-still', 'pale', 'unpeated'],
    url: 'https://crispmalt.com/en-us/malts/scottish-premium-pot-still-malt/',
    provenance: 'representative',
  },
  {
    id: 'fawcett-pearl-pale-ale',
    name: 'Pearl Pale Ale Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2.6,
    colorEbc: 5.7,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'One of the English heritage pale ale barleys kept alongside Maris Otter, and the softer of the pair: honey and biscuit, gentler than Halcyon and rounder than a modern cultivar. A base malt for bitters that are meant to taste of malt first.',
    tags: ['pearl', 'heritage', 'base', 'english', 'bitter', 'honey'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/pearl-pale-ale-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-halcyon-pale-ale',
    name: 'Halcyon Pale Ale Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2.6,
    colorEbc: 5.7,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The drier, cleaner of Fawcett\u2019s two heritage pale ale barleys. Where Pearl leans honeyed, Halcyon finishes crisp, which suits a bitter meant to be drunk by the pint rather than admired by the half. Fawcett publishes the same analysis band for both.',
    tags: ['halcyon', 'heritage', 'base', 'english', 'bitter', 'dry'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/halcyon-pale-ale-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-dark-crystal',
    name: 'Dark Crystal Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 83.2,
    colorEbc: 220,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The crystal malt that gives an ESB or an old ale its color and most of its character at once: dark toffee, raisin and a faint burnt-sugar edge, crystallized past ninety percent so the sweetness is real rather than implied.',
    tags: ['crystal', 'caramel', 'dark', 'english', 'esb', 'old-ale', 'porter'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/dark-crystal-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-oat-malt',
    name: 'Oat Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 60,
    moisturePercent: 5,
    colorLovibond: 2.6,
    colorEbc: 5.7,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Husked oat malt, the traditional kind: earthier and grainier than the naked oats the hazy brewers buy, and it brings its husk to the mash rather than gumming it up. Body and a soft cereal note in oatmeal stouts and milds.',
    tags: ['oat', 'malted', 'english', 'oatmeal-stout', 'mild', 'body'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/oat-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'simpsons-red-rye-crystal',
    name: 'Red Rye Crystal',
    maker: 'Simpsons',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 65,
    moisturePercent: 4,
    colorLovibond: 103.7,
    colorEbc: 273.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Rye crystallized rather than barley, giving bread crust and a sweet spice that no barley crystal reaches. Deep enough to redden a beer at 5-15 %, and at home in a rye ale or an autumn seasonal.',
    tags: ['crystal', 'rye', 'english', 'spicy', 'bread-crust'],
    url: 'https://www.simpsonsmalt.co.uk/our-malts/crystal-malts/red-rye-crystal/',
    provenance: 'spec-sheet',
  },
  {
    id: 'simpsons-roasted-barley',
    name: 'Roasted Barley',
    maker: 'Simpsons',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 62,
    moisturePercent: 3,
    colorLovibond: 604.2,
    colorEbc: 1600,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted barley taken very dark: bold, smoky and coffee-like, finishing dry and crisp rather than sweet. The lowest extract in the Simpsons range at 62 %, which is what a grain roasted this far costs you.',
    tags: ['roasted', 'english', 'stout', 'coffee', 'smoky', 'dry'],
    url: 'https://www.simpsonsmalt.co.uk/our-malts/roasted-malts/roasted-barley/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-golden-promise',
    name: 'Golden Promise Pale Ale Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2.6,
    colorEbc: 5.7,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The Scottish heritage barley, scarce enough that Fawcett call it a premium line: a soft, sweet wort with the body a cask ale needs and enough malt character to stand under a heavy hop rate. Fawcett publish the same analysis band for it as for Pearl and Halcyon.',
    tags: ['golden-promise', 'heritage', 'base', 'scottish', 'cask-ale', 'malty'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/golden-promise-pale-ale-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'pauls-keep-flying',
    name: 'Keep Flying',
    maker: 'Pauls Malt',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.9,
    moisturePercent: 4.5,
    colorLovibond: 2.7,
    colorEbc: 6.0,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pauls Malt’s Maris Otter, and the extract is the headline: over 306 lt°/kg, which is high for the variety and part of why a maltster this size can sell it against Crisp and Simpsons. Bready and rounded, for bitters and cask ale.',
    tags: ['maris-otter', 'base', 'english', 'bitter', 'malty'],
    provenance: 'spec-sheet',
  },
  {
    id: 'pauls-albino-whale',
    name: 'Albino Whale',
    maker: 'Pauls Malt',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81.9,
    moisturePercent: 4.5,
    colorLovibond: 1.5,
    colorEbc: 2.8,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale ale malt kilned as light as the style allows — 2 to 3.5 EBC — so the malt gives body and enzyme without the color. The base for a modern pale that should be straw rather than gold.',
    tags: ['pale-ale', 'base', 'low-colour', 'ipa', 'english'],
    provenance: 'spec-sheet',
  },
  {
    id: 'pauls-pale-whale',
    name: 'Pale Whale',
    maker: 'Pauls Malt',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81.9,
    moisturePercent: 4.5,
    colorLovibond: 2.5,
    colorEbc: 5.5,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The standard English pale ale malt of the range: 310 lt°/kg of extract and a 4 to 7 EBC color that leaves room for hops or crystal. A grist can be built entirely on it.',
    tags: ['pale-ale', 'base', 'english', 'bitter', 'workhorse'],
    provenance: 'spec-sheet',
  },
  {
    id: 'pauls-odd-amphibian',
    name: 'Odd Amphibian',
    maker: 'Pauls Malt',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81.4,
    moisturePercent: 4.5,
    colorLovibond: 2.0,
    colorEbc: 4.1,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The lager malt, kilned low and cured gently, with the highest nitrogen allowance in the base range to carry a long cold fermentation. Clean and quiet under noble hops.',
    tags: ['lager', 'base', 'pilsner', 'clean', 'crisp'],
    provenance: 'spec-sheet',
  },
  {
    id: 'pauls-washy-frothy',
    name: 'Washy Frothy',
    maker: 'Pauls Malt',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81.9,
    moisturePercent: 5,
    colorLovibond: 2.1,
    colorEbc: 4.4,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted wheat for weissbier, wit and anything wanting a firmer head. Huskless, so it slows a mash in quantity; Pauls allow the widest color band in the range, 2 to 7 EBC, because wheat kilns unevenly.',
    tags: ['wheat', 'base', 'weissbier', 'witbier', 'head-retention'],
    provenance: 'spec-sheet',
  },
  {
    id: 'pauls-muscley',
    name: 'Muscley',
    maker: 'Pauls Malt',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79.8,
    moisturePercent: 4.5,
    colorLovibond: 5.9,
    colorEbc: 14.5,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Munich malt at 13 to 16 EBC with enough diastatic power left to convert itself, so it can carry more of a grist than a specialty malt usually does. Toasted crust for märzen, bock and amber ale.',
    tags: ['munich', 'kilned', 'malty', 'marzen', 'bock'],
    provenance: 'spec-sheet',
  },
  {
    id: 'pauls-miserable-fish',
    name: 'Miserable Fish',
    maker: 'Pauls Malt',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77.4,
    moisturePercent: 4.5,
    colorLovibond: 17.4,
    colorEbc: 45.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Melanoidin malt, stewed rather than simply kilned, for the deep bready sweetness and red hue that a long decoction would otherwise be needed to get. A few percent goes a long way.',
    tags: ['melanoidin', 'kilned', 'malty', 'red-ale', 'intense'],
    provenance: 'spec-sheet',
  },
  {
    id: 'pauls-dead-straight',
    name: 'Dead Straight',
    maker: 'Pauls Malt',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75.1,
    moisturePercent: 5.5,
    colorLovibond: 24.9,
    colorEbc: 65.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'English amber malt: dry, biscuity and faintly bitter, the traditional seasoning of porters and brown ales before crystal malt took over. Its enzymes are gone, so it needs a base malt under it.',
    tags: ['amber', 'toasted', 'biscuit', 'porter', 'brown-ale', 'english'],
    provenance: 'spec-sheet',
  },
  {
    id: 'pauls-reliable-lighthouse-light-chocolate',
    name: 'Reliable Lighthouse Light Chocolate',
    maker: 'Pauls Malt',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 73.2,
    moisturePercent: 4,
    colorLovibond: 188.4,
    colorEbc: 499.9,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The gentler of the two Reliable Lighthouse roasts at 400 to 600 EBC: cocoa and a soft roast without the ash of a full chocolate malt. For porters that want color and restraint.',
    tags: ['chocolate', 'roasted', 'light-chocolate', 'porter', 'brown-ale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'pauls-reliable-lighthouse-chocolate',
    name: 'Reliable Lighthouse Chocolate',
    maker: 'Pauls Malt',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 73.2,
    moisturePercent: 4,
    colorLovibond: 338.9,
    colorEbc: 900.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Chocolate malt proper, 800 to 1000 EBC, carrying the cocoa and dark-toast character a stout is built on. Pauls sell both roast levels under the same name, which is worth knowing when ordering.',
    tags: ['chocolate', 'roasted', 'porter', 'stout', 'cocoa'],
    provenance: 'spec-sheet',
  },
  {
    id: 'pauls-angry-bear',
    name: 'Angry Bear',
    maker: 'Pauls Malt',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 73.2,
    moisturePercent: 4,
    colorLovibond: 545.7,
    colorEbc: 1450.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Black malt for color and a hard, dry edge. Five percent is plenty; past that the astringency arrives before the flavor does.',
    tags: ['black-malt', 'roasted', 'stout', 'porter', 'colour', 'sharp'],
    provenance: 'spec-sheet',
  },
  {
    id: 'pauls-angry-angler',
    name: 'Angry Angler',
    maker: 'Pauls Malt',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 73.2,
    moisturePercent: 4,
    colorLovibond: 564.5,
    colorEbc: 1500.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Roasted unmalted barley, the coffee-and-dry-bitterness backbone of an Irish stout. Sharper than black malt and the reason a dry stout finishes the way it does.',
    tags: ['roasted-barley', 'unmalted', 'stout', 'dry-stout', 'coffee'],
    provenance: 'spec-sheet',
  },
  {
    id: 'pauls-captains-classic',
    name: 'Captains Classic',
    maker: 'Pauls Malt',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.1,
    moisturePercent: 7.5,
    colorLovibond: 11.7,
    colorEbc: 29.9,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest of the crystal range at 25 to 35 EBC, for body and head without color. The high moisture allowance, 7.5 percent, is normal for a stewed malt and not a fault.',
    tags: ['caramalt', 'crystal', 'light', 'body', 'head-retention'],
    provenance: 'spec-sheet',
  },
  {
    id: 'pauls-lucky-squid',
    name: 'Lucky Squid',
    maker: 'Pauls Malt',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.1,
    moisturePercent: 6,
    colorLovibond: 45.6,
    colorEbc: 120.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Light crystal at 110 to 130 EBC: toffee and a copper tint, the seasoning of an ordinary bitter. Steeps without mashing.',
    tags: ['crystal', 'caramel', 'light-crystal', 'bitter', 'toffee'],
    provenance: 'spec-sheet',
  },
  {
    id: 'pauls-mark-5',
    name: 'Mark 5',
    maker: 'Pauls Malt',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.1,
    moisturePercent: 5.5,
    colorLovibond: 64.4,
    colorEbc: 170.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The middle crystal, 150 to 190 EBC, where caramel sweetness and color are in balance. The default choice for an ESB or an amber ale.',
    tags: ['crystal', 'caramel', 'medium-crystal', 'esb', 'toffee'],
    provenance: 'spec-sheet',
  },
  {
    id: 'pauls-deep-diver',
    name: 'Deep Diver',
    maker: 'Pauls Malt',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.1,
    moisturePercent: 5,
    colorLovibond: 85.0,
    colorEbc: 224.9,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Dark crystal at 200 to 250 EBC, moving from toffee towards raisin and burnt sugar. Ten percent is the ceiling before the sweetness turns cloying.',
    tags: ['crystal', 'caramel', 'dark-crystal', 'raisin', 'old-ale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'pauls-shell-shocker',
    name: 'Shell Shocker',
    maker: 'Pauls Malt',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.1,
    moisturePercent: 5,
    colorLovibond: 117.0,
    colorEbc: 310.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest crystal Pauls make, 270 to 350 EBC: prune, treacle and a bitterness at the edge of roast without ever having been roasted.',
    tags: ['crystal', 'caramel', 'extra-dark', 'prune', 'old-ale', 'porter'],
    provenance: 'spec-sheet',
  },
  {
    id: 'pauls-rough-tide',
    name: 'Rough Tide',
    maker: 'Pauls Malt',
    origin: 'United Kingdom',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 80.6,
    moisturePercent: 12,
    colorLovibond: 1.8,
    colorEbc: 3.6,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Torrefied wheat — heated until the grain bursts, so it gelatinises in the mash without malting. Head retention and body in a bitter, and no enzymes of its own.',
    tags: ['torrefied', 'wheat', 'unmalted', 'head-retention', 'body'],
    provenance: 'spec-sheet',
  },
  {
    id: 'pauls-greedy-goats',
    name: 'Greedy Goats',
    maker: 'Pauls Malt',
    origin: 'United Kingdom',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 77.2,
    moisturePercent: 12,
    colorLovibond: 1.6,
    colorEbc: 3.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Rolled oats for the silkiness they lend a stout or a hazy pale. Unmalted and glucan-rich, so rice hulls earn their place past about ten percent of the grist.',
    tags: ['oats', 'rolled', 'unmalted', 'mouthfeel', 'oatmeal-stout', 'neipa'],
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-brown',
    name: 'Brown Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 69.9,
    moisturePercent: 4.5,
    colorLovibond: 71,
    colorEbc: 187,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "The brown malt English porter recipes name outright, and drier and more biscuit-like than the crystal it is often mistaken for. Fawcett roast it a shade past their Amber, so it carries toast and a faint coffee edge with none of a chocolate malt's bite. Around seventy Lovibond, and a few percent is enough to turn a mild or a London porter.",
    tags: ['roasted', 'brown', 'english', 'porter', 'mild', 'toast', 'historical'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/brown-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-chocolate',
    name: 'Chocolate Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 69.9,
    moisturePercent: 4.5,
    colorLovibond: 420,
    colorEbc: 1111.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "The full chocolate roast, and the darker half of the pair their Pale Chocolate opens: deep roast coffee with a dark-chocolate edge underneath. Most of a porter's or a stout's color comes from a grain like this, and most of its roast character too.",
    tags: ['roasted', 'chocolate', 'english', 'porter', 'stout', 'coffee'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/chocolate-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-black',
    name: 'Black Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 69.9,
    moisturePercent: 4.5,
    colorLovibond: 545,
    colorEbc: 1443.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Taken to the far end of the drum, and sharp with it — color and dry, acrid roast rather than anything with sweetness left in. Two or three percent blackens a robust porter, an imperial stout or a black IPA; much beyond that and the finish turns ashy.',
    tags: ['roasted', 'black', 'english', 'stout', 'porter', 'acrid', 'colour'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/black-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-roast-rye',
    name: 'Roast Rye Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 64.8,
    moisturePercent: 4.5,
    colorLovibond: 245.7,
    colorEbc: 649.9,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Rye taken to a dark roast, and lower in color than barley reaches on the same treatment. Dry biscuit and liquorice rather than coffee, with rye pepper underneath, so it darkens a beer in a way that reads as spice instead of char. Fawcett print no ASBC figure for it, so the color here is converted from their EBC band.',
    tags: ['roasted', 'rye', 'english', 'liquorice', 'spicy', 'dark'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/roast-rye-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-roast-wheat',
    name: 'Roast Wheat Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 69.9,
    moisturePercent: 4.5,
    colorLovibond: 325,
    colorEbc: 859.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Wheat roasted dark, which softens the roast next to a roasted barley and keeps the grip on the head that wheat is bought for. It suits a dark wheat beer, and it is a way of adding color to a stout without a second acrid note fighting the first.',
    tags: ['roasted', 'wheat', 'english', 'head-retention', 'dark', 'stout'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/roast-wheat-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-red-crystal',
    name: 'Red Crystal Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 68.7,
    moisturePercent: 4.5,
    colorLovibond: 151.4,
    colorEbc: 399.9,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The crystal Fawcett stew for color: ruby red with a pronounced bonfire-toffee sweetness and a burnt-sugar finish. Published at 350-450 EBC, which is dark enough that a small share of the grist sets the color of a red ale or a strong bitter on its own.',
    tags: ['crystal', 'caramel', 'red', 'english', 'toffee', 'strong-ale'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/red-crystal-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-pale-caramalt',
    name: 'Pale Caramalt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 69.9,
    moisturePercent: 7.5,
    colorLovibond: 6.5,
    colorEbc: 16,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Paler and sweeter than the Caramalt beside it, stewed only far enough for a golden cast and an almost honeyed sweetness. Crystallized to a lower degree than the rest of the range, so what it lends is sweetness and a little color rather than much body.',
    tags: ['crystal', 'caramel', 'pale', 'english', 'honey', 'golden'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/pale-caramalt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-crystal-wheat',
    name: 'Crystal Wheat Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 69.9,
    moisturePercent: 5,
    colorLovibond: 60,
    colorEbc: 157.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Wheat stewed and kilned as a crystal, which comes out drier than the barley version and keeps wheat’s hold on the head. Deep caramel and toffee for a dunkelweizen, or an unexpected turn in a stout that wants sweetness and foam at once.',
    tags: ['crystal', 'caramel', 'wheat', 'english', 'toffee', 'head-retention'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/crystal-wheat-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-dextrin',
    name: 'Dextrin Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 67.4,
    moisturePercent: 5,
    colorLovibond: 2.2,
    colorEbc: 4.6,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malt held back on purpose, under-modified so the wort carries more of the long sugars yeast cannot reach. Body, mouthfeel and foam stability with almost no color and no flavor to argue with the rest of the grist. Fawcett publish only an IoB analysis for it, so the color here is converted from their EBC figure.',
    tags: ['dextrin', 'carapils', 'body', 'english', 'head-retention', 'mouthfeel'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/dextrin-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-extra-pale-ale',
    name: 'Extra Pale Ale Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79.8,
    moisturePercent: 4.5,
    colorLovibond: 1.9,
    colorEbc: 3.8,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Modern two-row kilned lighter than their Spring Pale Ale malt, for a beer that wants the extract and the enzyme without the color. The base for a pale, hop-forward ale where the malt is meant to stay out of the way.',
    tags: ['base', 'pale', 'english', 'extra-pale', 'ipa', 'modern'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/extra-pale-ale-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-lager-pilsner',
    name: 'Lager / Pilsner Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.6,
    moisturePercent: 4.5,
    colorLovibond: 1.9,
    colorEbc: 3.8,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Fully modified and gently kilned, the palest base Fawcett make and the one a lager or a crisp hop-forward ale is built on. Clean rather than characterful, which is the whole point of it.',
    tags: ['base', 'lager', 'pilsner', 'english', 'pale', 'clean'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/lager-pilsner-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-maris-otter-low-colour',
    name: 'Maris Otter Low Colour Ale Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.1,
    moisturePercent: 4,
    colorLovibond: 1.9,
    colorEbc: 3.8,
    diastaticPowerLintner: 45,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Maris Otter kilned short of its usual color, for brewers who want the barley’s biscuit and body in a beer that has to stay pale. Fawcett publish it at 3-5 EBC against 5-7.5 for their standard Maris Otter; otherwise it is the same floor-malted barley.',
    tags: ['base', 'maris-otter', 'heritage', 'english', 'pale', 'low-colour'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/maris-otter-low-colour-ale-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-mild-ale',
    name: 'Mild Ale Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79.3,
    moisturePercent: 4,
    colorLovibond: 3.6,
    colorEbc: 8.3,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A base malt kilned past pale ale color, so a mild or a dark ale arrives with depth and a rounder, sweeter malt character before a single specialty grain goes in. It converts a grist on its own and can be all of one. Fawcett print no ASBC figure for it, so the color here is converted from their EBC band.',
    tags: ['base', 'mild', 'english', 'malty', 'dark-mild', 'sweet'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/mild-ale-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-spring-pale-ale',
    name: 'Spring Pale Ale Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.6,
    moisturePercent: 4,
    colorLovibond: 2.8,
    colorEbc: 6.2,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The modern base malt of the range, made from spring barleys such as Laureate rather than a heritage variety: more extract, cleaner and crisper than Maris Otter, and the sensible choice when the hops or the yeast are meant to be the story.',
    tags: ['base', 'pale', 'english', 'laureate', 'spring-barley', 'modern'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/spring-pale-ale-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-peated',
    name: 'Peated Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.6,
    moisturePercent: 5.5,
    colorLovibond: 2.5,
    colorEbc: 5.4,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale malt dried over peat smoke, moderately rather than heavily: smoky, faintly medicinal, and traditional in Scotch whisky long before brewers borrowed it. Pale and enzymatic, so smoke is the only thing it changes, and a small share of a grist is plenty. Fawcett’s ASBC tab for it prints figures that belong to other rows, so only its Lovibond band is read here.',
    tags: ['peated', 'smoked', 'phenolic', 'english', 'whisky', 'scottish'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/peated-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-munich',
    name: 'Munich Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78.8,
    moisturePercent: 4,
    colorLovibond: 10.5,
    colorEbc: 26.6,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned hard for color and a heavy, bready sweetness, and the malt a Märzen or a bock gets its backbone from. It brings mouthfeel and depth that no crystal quite imitates, and it usually makes up a large part of a grist rather than all of one.',
    tags: ['munich', 'kilned', 'english', 'marzen', 'bock', 'malty'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/munich-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-vienna',
    name: 'Vienna Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78.8,
    moisturePercent: 4,
    colorLovibond: 5.5,
    colorEbc: 13.4,
    diastaticPowerLintner: 46,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A step past pale in color and aroma, with a sweet toastiness that suits a Vienna lager and gives a fuller-bodied ale its amber cast. Gentler than Munich, and comfortable across a much larger share of the grist for it.',
    tags: ['vienna', 'kilned', 'english', 'amber', 'lager', 'malty'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/vienna-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-rye',
    name: 'Rye Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 75.1,
    moisturePercent: 4.5,
    colorLovibond: 3.25,
    colorEbc: 7.4,
    diastaticPowerLintner: 30,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Floor-malted rye, huskless and full-flavored: peppery, dry and unmistakable in a rye IPA or a bitter that wants an edge. Bring rice hulls if it is more than a modest share of the mash, because nothing in it will help the bed run.',
    tags: ['rye', 'base', 'english', 'floor-malted', 'spicy', 'rye-ipa'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/rye-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-wheat',
    name: 'Wheat Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80.3,
    moisturePercent: 4.5,
    colorLovibond: 1.9,
    colorEbc: 3.8,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Floor-malted wheat for head retention and a light malt sweetness — the staple of a hefeweizen or a wit grist, and a useful few percent in almost anything that wants firmer foam. Huskless, so it leans on the barley around it to drain.',
    tags: ['wheat', 'base', 'english', 'floor-malted', 'hefeweizen', 'head-retention'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/wheat-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-flaked-barley',
    name: 'Flaked Barley',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 64.8,
    moisturePercent: 10,
    colorLovibond: 1.9,
    colorEbc: 3.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted barley rolled and gelatinised, so it goes straight into the mash for body, foam and a smooth cereal note. The grain behind a dry stout’s texture. Fawcett file it among their malts and their own page calls it unmalted, which is the accurate half of that.',
    tags: ['adjunct', 'barley', 'flaked', 'unmalted', 'english', 'stout', 'head-retention'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/flaked-barley-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-flaked-maize',
    name: 'Flaked Maize',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 75.1,
    moisturePercent: 10,
    colorLovibond: 1.9,
    colorEbc: 3.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Rolled and gelatinised maize, close to pure starch: it lightens the body and the color of a beer and leaves very little flavor of its own behind. The classic cream ale and pre-Prohibition lager adjunct, and unmalted whatever the name on the page says.',
    tags: ['adjunct', 'maize', 'corn', 'flaked', 'unmalted', 'cream-ale', 'light-body'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/flaked-maize-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-crystal-oat',
    name: 'Crystal Oat Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 51.8,
    moisturePercent: 5,
    colorLovibond: 36,
    colorEbc: 94.2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Oats stewed and kilned as a crystal, so the creaminess oats are bought for arrives with caramelized sugar behind it: toffee, silk and a little color at once. Its hull stays on through malting and yields nothing, which is why the extract sits near half its weight and a grist has to be planned around that.',
    tags: ['crystal', 'caramel', 'oat', 'english', 'toffee', 'mouthfeel', 'neipa'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/crystal-oat-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'fawcett-toasted-oat',
    name: 'Toasted Oat Malt',
    maker: 'Thomas Fawcett',
    origin: 'United Kingdom',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 51.8,
    moisturePercent: 4.5,
    colorLovibond: 27.5,
    colorEbc: 71.7,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Whole oats taken to a light roast rather than a stew: biscuit sweetness, a creamy mouthfeel and the standing haze a modern stout or a New England IPA is after. Roasted only far enough to reach the high twenties in Lovibond, so it darkens very little. Hull-on like the rest of Fawcett's oats, and about half its weight in extract for it.",
    tags: ['roasted', 'oat', 'toasted', 'english', 'biscuit', 'mouthfeel', 'neipa'],
    url: 'https://www.fawcett-maltsters.co.uk/malt/toasted-oat-malt/',
    provenance: 'spec-sheet',
  },
];
